import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStoryStore } from '../stores/storyStore'
import settingsService from '../services/settingsService'
import { RealAIService } from '../services/realAiService'
import { AdvancedJsonRepair } from '../utils/advancedJsonRepair'
import UltimateJsonParser from '../utils/ultimateJsonParser'
import { DoubaoImageServiceV2 } from '../services/doubaoImageServiceV2.js'
import { ImageProxyService } from '../services/imageProxyService.js'
import localCloudService from '../services/localCloudService'
import { calculateNodePositions } from '../utils/storyLayoutAlgorithm.js'
import TagGeneratorService from '../services/tagGeneratorService'
import SmartImage from './SmartImage.jsx'
import toast from 'react-hot-toast'

// 图标组件
const SendIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
  </svg>
)

const AIGuidedCreation = ({ userDescription, onClose, editStoryId = null }) => {
  const navigate = useNavigate()
  const { createStory, updateStory, getStory } = useStoryStore()
  const chatEndRef = useRef(null)
  
  // 编辑模式状态
  const [isEditMode, setIsEditMode] = useState(false)
  const [editingStoryId, setEditingStoryId] = useState(editStoryId)
  
  // 玩创模式导入数据
  const [importedPlayCreateData, setImportedPlayCreateData] = useState(null)
  
  // 状态管理
  const [currentStep, setCurrentStep] = useState(0)
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: '你好！我是AI创作助手。请告诉我你的故事想法，我会帮你一步步完善它。',
      timestamp: new Date()
    }
  ])
  const [currentMessage, setCurrentMessage] = useState('')
  const [isChatting, setIsChatting] = useState(false)
  const [forceUpdate, setForceUpdate] = useState(0) // 强制更新计数器
  
  // 选项和内容状态
  const [currentOptions, setCurrentOptions] = useState([])
  const [selectedOption, setSelectedOption] = useState(null)
  const [currentGeneratedContent, setCurrentGeneratedContent] = useState(null)
  const [confirmedData, setConfirmedData] = useState({})
  
  // 更新角色图像的辅助函数
  const updateCharacterImage = useCallback((characterId, imageUrl) => {
    console.log('🔄 更新角色图像:', { characterId, imageUrl })
    
    setCurrentGeneratedContent(prev => {
      if (!prev || !prev.characters) {
        console.warn('⚠️ currentGeneratedContent或characters为空')
        return prev
      }
      
      const updatedCharacters = prev.characters.map(char => 
        char.id === characterId ? { ...char, generatedImage: imageUrl } : char
      )
      
      console.log('✅ 角色图像状态已更新:', {
        characterId,
        imageUrl,
        updatedChar: updatedCharacters.find(c => c.id === characterId)
      })
      
      return {
        ...prev,
        characters: updatedCharacters
      }
    })
    
    // 强制重新渲染
    setForceUpdate(prev => prev + 1)
  }, [])
  
  // 更新场景图像的辅助函数
  const updateSceneImage = useCallback((sceneId, imageUrl) => {
    console.log('🔄 更新场景图像:', { sceneId, imageUrl })
    
    setCurrentGeneratedContent(prev => {
      if (!prev || !prev.scenes) {
        console.warn('⚠️ currentGeneratedContent或scenes为空')
        return prev
      }
      
      const updatedScenes = prev.scenes.map(scene => 
        scene.id === sceneId ? { ...scene, generatedImage: imageUrl } : scene
      )
      
      console.log('✅ 场景图像状态已更新:', {
        sceneId,
        imageUrl,
        updatedScene: updatedScenes.find(s => s.id === sceneId)
      })
      
      return {
        ...prev,
        scenes: updatedScenes
      }
    })
    
    // 强制重新渲染
    setForceUpdate(prev => prev + 1)
  }, [])
  
  // 对话历史（用于AI上下文）
  const [conversationHistory, setConversationHistory] = useState([])
  
  // 步骤定义
  const steps = [
    { id: 1, key: 'idea', name: '创意想法', icon: '💡', needsChoice: false },
    { id: 2, key: 'genre', name: '题材分析', icon: '🎭', needsChoice: true },
    { id: 3, key: 'theme', name: '主题选择', icon: '🎯', needsChoice: true },
    { id: 4, key: 'style', name: '美术风格', icon: '🎨', needsChoice: true },
    { id: 5, key: 'outline', name: '故事大纲与世界观', icon: '📝', needsChoice: true },
    { id: 6, key: 'characters', name: '角色设计', icon: '👥', needsChoice: false },
    { id: 7, key: 'scenes', name: '场景设计', icon: '🏞️', needsChoice: false },
    { id: 8, key: 'storyboard', name: '分镜设计', icon: '🎬', needsChoice: false }
  ]

  // 真实生成图片 - 使用通用图像配置
  const generateImage = async (prompt, aspectRatio = '16:9') => {
    try {
      console.log('🎨 开始生成图像:', prompt.substring(0, 50) + '...', '比例:', aspectRatio)
      
      // 直接使用通用图像生成配置
      const config = settingsService.getImageGenerationConfig()
      if (!config.apiKey || !config.apiUrl) {
        console.warn('⚠️ 图像生成API未配置，使用模拟图像')
        return await mockGenerateImage(prompt)
      }
      
      // 检查是否为豆包模型
      if (config.model === 'doubao-seedream-4-0' || config.model === 'doubao-seedream-4-0-250828') {
        console.log('✅ 使用豆包API生成图像，比例:', aspectRatio)
        // 调用豆包API生成图像
        const imageUrl = await DoubaoImageServiceV2.generateImage(prompt, {
          maxImages: 1,
          size: '2K',
          aspectRatio: aspectRatio, // 传递比例参数
          watermark: true
        })
        
        console.log('✅ 图像生成成功:', imageUrl)
        return imageUrl
      } else {
        console.warn('⚠️ 当前模型不是豆包模型，使用模拟图像')
        return await mockGenerateImage(prompt)
      }
    } catch (error) {
      console.error('❌ 图像生成失败:', error)
      toast.error('图像生成失败: ' + error.message)
      
      // 失败时使用模拟图像
      console.log('🔄 使用模拟图像作为备用')
      return await mockGenerateImage(prompt)
    }
  }

  // 模拟生成图片（备用方案）
  const mockGenerateImage = async (prompt) => {
    await new Promise(resolve => setTimeout(resolve, 1500))
    return `https://via.placeholder.com/400x600/667eea/ffffff?text=${encodeURIComponent(prompt.substring(0, 20))}`
  }

  // 模拟选择配音
  const mockSelectVoice = (character) => {
    toast('配音选择功能\n\n角色：' + character.name + '\n配音类型：' + character.voiceType, {
      icon: '🎤',
      duration: 2000
    })
  }

  // 模拟生成音乐
  const mockGenerateMusic = (scene) => {
    toast('音乐生成功能\n\n场景：' + scene.name + '\n音乐风格：' + scene.musicStyle, {
      icon: '🎵',
      duration: 2000
    })
  }

  // 自动滚动到底部
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages, currentOptions, currentGeneratedContent])

  // 保存聊天记录到localStorage
  const saveChatHistory = useCallback((messages) => {
    try {
      // 为新故事生成唯一ID，避免继承旧对话
      const storyId = editingStoryId || `new-${Date.now()}`
      const chatKey = `ai-guided-chat-${storyId}`
      localStorage.setItem(chatKey, JSON.stringify(messages))
      // 静默模式：只在调试时输出成功日志
      if (process.env.NODE_ENV === 'development') {
        console.debug('✅ 聊天记录已保存:', chatKey, messages.length, '条消息')
      }
    } catch (error) {
      // 静默失败：聊天记录不是核心功能，失败不影响使用
      if (process.env.NODE_ENV === 'development') {
        console.debug('⚠️ 保存聊天记录失败（已忽略）:', error.message)
      }
    }
  }, [editingStoryId])

  // 加载聊天记录从localStorage
  const loadChatHistory = useCallback(() => {
    try {
      // 只有在编辑模式下才加载聊天记录
      if (!editingStoryId) {
        if (process.env.NODE_ENV === 'development') {
          console.debug('🆕 新故事模式，不加载旧聊天记录')
        }
        return null
      }
      
      const chatKey = `ai-guided-chat-${editingStoryId}`
      const saved = localStorage.getItem(chatKey)
      if (saved) {
        const messages = JSON.parse(saved)
        // 确保时间戳是Date对象
        const parsedMessages = messages.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }))
        setChatMessages(parsedMessages)
        if (process.env.NODE_ENV === 'development') {
          console.debug('✅ 聊天记录已加载:', chatKey, parsedMessages.length, '条消息')
        }
        return parsedMessages
      }
    } catch (error) {
      // 静默失败
      if (process.env.NODE_ENV === 'development') {
        console.debug('⚠️ 加载聊天记录失败（已忽略）:', error.message)
      }
    }
    return null
  }, [editingStoryId])

  // 当chatMessages变化时自动保存
  useEffect(() => {
    if (chatMessages.length > 1) { // 避免保存初始消息
      saveChatHistory(chatMessages)
    }
  }, [chatMessages, saveChatHistory])
  
  // 监听confirmedData变化，强制刷新UI
  useEffect(() => {
    console.log('🔔 confirmedData已变化:', {
      keys: Object.keys(confirmedData),
      count: Object.keys(confirmedData).length,
      timestamp: Date.now()
    })
    // 每次变化都增加forceUpdate，确保UI刷新
    setForceUpdate(prev => prev + 1)
  }, [confirmedData])

  // 加载编辑模式数据
  const loadEditModeData = useCallback(async () => {
    if (!editingStoryId) return
    
    try {
      // 优先尝试从编辑模式数据加载（最新的数据）
      // 首先尝试从IndexedDB加载（编辑器保存的位置）
      let savedData = null
      try {
        savedData = await localCloudService.loadFromIndexedDB(`guided-edit-${editingStoryId}`)
        console.log('✅ 从IndexedDB加载编辑模式数据:', savedData)
      } catch (error) {
        console.warn('⚠️ IndexedDB加载失败，尝试localStorage:', error)
        // 备用方案：从localStorage加载
        const savedDataStr = localStorage.getItem(`guided-edit-${editingStoryId}`)
        if (savedDataStr) {
          savedData = JSON.parse(savedDataStr)
          console.log('✅ 从localStorage加载编辑模式数据:', savedData)
        }
      }
      
      if (!savedData) {
        console.warn('⚠️ 未找到编辑模式数据，尝试从故事数据加载')
        
        // 备用方案：从故事数据中恢复
        const storyData = getStory(editingStoryId)
        if (storyData && storyData.metadata) {
          // 恢复聊天记录
          if (storyData.metadata.chatHistory && storyData.metadata.chatHistory.length > 0) {
            if (process.env.NODE_ENV === 'development') {
              console.debug('✅ 从故事数据恢复聊天记录:', storyData.metadata.chatHistory.length, '条消息')
            }
            const restoredMessages = storyData.metadata.chatHistory.map(msg => ({
              ...msg,
              timestamp: new Date(msg.timestamp)
            }))
            setChatMessages(restoredMessages)
            
            // 恢复对话历史
            if (storyData.metadata.conversationHistory) {
              setConversationHistory(storyData.metadata.conversationHistory)
            }
          }
          
          // 恢复已确认的数据
          const confirmedDataFromStory = {}
          Object.keys(storyData.metadata).forEach(key => {
            if (['genre', 'theme', 'style', 'outline', 'characters', 'scenes', 'storyboard'].includes(key)) {
              confirmedDataFromStory[key] = storyData.metadata[key]
            }
          })
          if (Object.keys(confirmedDataFromStory).length > 0) {
            setConfirmedData(confirmedDataFromStory)
            
            // 计算当前步骤
            const completedSteps = Object.keys(confirmedDataFromStory).length
            setCurrentStep(Math.min(completedSteps, steps.length - 1))
          }
          
          setIsEditMode(true)
          return
        }
        
        console.warn('⚠️ 未找到任何可恢复的数据')
        return
      }
      console.log('✅ 加载编辑模式数据:', savedData)
      
      // 设置为编辑模式
      setIsEditMode(true)
      
      // 恢复已确认的数据
      if (savedData.confirmedData) {
        setConfirmedData(savedData.confirmedData)
        
        // 计算当前步骤（最后一个完成的步骤）
        const completedSteps = Object.keys(savedData.confirmedData).length
        setCurrentStep(Math.min(completedSteps, steps.length - 1))
      }
      
      // 添加欢迎消息（如果当前没有聊天记录）
      if (chatMessages.length <= 1) { // 只有初始消息或没有消息
        setChatMessages([
          {
            role: 'assistant',
            content: `欢迎回来！正在编辑故事「${savedData.storyTitle}」。你可以继续完善策划案，或修改已有内容。`,
            timestamp: new Date()
          }
        ])
      }
      
      // 清除加载的数据（避免重复加载）
      try {
        await localCloudService.deleteFromIndexedDB(`guided-edit-${editingStoryId}`)
        console.log('✅ 已清除IndexedDB中的编辑模式数据')
      } catch (error) {
        console.warn('⚠️ 清除IndexedDB数据失败:', error)
      }
      // 同时清除localStorage中的备用数据
      localStorage.removeItem(`guided-edit-${editingStoryId}`)
      
    } catch (error) {
      console.error('❌ 加载编辑模式数据失败:', error)
      toast.error('加载数据失败: ' + error.message)
    }
  }, [editingStoryId, getStory])
  
  // 初始化 - 检查是否为编辑模式
  useEffect(() => {
    // 优先检查URL参数中的编辑标记
    const urlParams = new URLSearchParams(window.location.search)
    const editId = urlParams.get('edit')
    
    if (editId) {
      setEditingStoryId(editId)
      // 先尝试加载聊天记录
      const loadedMessages = loadChatHistory()
      if (!loadedMessages) {
        // 如果没有聊天记录，再加载编辑模式数据
        loadEditModeData().catch(console.error)
      }
    } else if (editStoryId) {
      // 先尝试加载聊天记录
      const loadedMessages = loadChatHistory()
      if (!loadedMessages) {
        // 如果没有聊天记录，再加载编辑模式数据
        loadEditModeData().catch(console.error)
      }
    } else {
      // 检查是否有玩创模式导入数据
      const playCreateData = sessionStorage.getItem('playcreate-converted-data')
      if (playCreateData) {
        try {
          const parsed = JSON.parse(playCreateData)
          setImportedPlayCreateData(parsed)
          // 初始化聊天记录和确认数据
          setChatMessages([{
            role: 'assistant',
            content: `已导入来自玩创模式的对话记录，包含${parsed.scenes?.length || 0}个场景。让我们继续完善这个故事吧！`,
            timestamp: new Date()
          }])
          // 直接设置确认数据
          if (parsed.outline) setConfirmedData(prev => ({ ...prev, outline: parsed.outline }))
          if (parsed.characters) setConfirmedData(prev => ({ ...prev, characters: { characters: parsed.characters } }))
          if (parsed.scenes) setConfirmedData(prev => ({ ...prev, scenes: { scenes: parsed.scenes } }))
          if (parsed.storyboard) setConfirmedData(prev => ({ ...prev, storyboard: parsed.storyboard }))
          // 清除sessionStorage中的数据
          sessionStorage.removeItem('playcreate-converted-data')
          toast.success('已导入玩创模式数据')
        } catch (error) {
          console.error('解析玩创模式数据失败:', error)
        }
      }
      
      // 新创建模式：清空聊天记录，确保独立对话
      console.log('🆕 新故事模式，初始化独立对话')
      if (!playCreateData) {
        setChatMessages([]) // 清空聊天记录
      }
      
      if (userDescription && currentStep === 0) {
        // 添加用户消息
        setChatMessages([{
          role: 'user',
          content: userDescription,
          timestamp: new Date()
        }])
        
        // 自动进入题材分析
        setTimeout(() => {
          setCurrentStep(1)
          generateOptionsForStep(1)
        }, 500)
      }
    }
  }, [userDescription, editStoryId, loadEditModeData, loadChatHistory])

  // 调用真实AI生成内容（流式）
  const callRealAIStreaming = async (prompt, context = '', onAdviceChunk = null) => {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig || !languageConfig.apiKey) {
        console.warn('⚠️ 未配置AI服务，使用备用方案')
        return null
      }

      // 根据模型动态调整token限制
      const modelName = languageConfig.model?.toLowerCase() || ''
      let maxTokens = 4096 // 默认值
      
      if (modelName.includes('gpt-4')) {
        maxTokens = 4096
      } else if (modelName.includes('deepseek')) {
        maxTokens = 4096 // DeepSeek流式调用使用4096
      } else if (modelName.includes('claude')) {
        maxTokens = 4096
      } else if (modelName.includes('gemini')) {
        maxTokens = 4096
      }
      
      console.log('🤖 使用真实AI生成（流式）:', languageConfig.provider, languageConfig.model, 'maxTokens:', maxTokens)

      // 构建完整的提示词
      let fullPrompt = '你是一位专业的互动故事创作顾问。你的任务是帮助用户创作高质量的互动式故事。请严格按照指定格式返回结果。注重互动内容的专业性，包括对话交互、分支结构、多结局设计、情绪走向等。\n\n'
      
      if (conversationHistory.length > 0) {
        fullPrompt += '之前的对话:\n'
        conversationHistory.forEach(msg => {
          fullPrompt += `${msg.role === 'user' ? '用户' : 'AI'}: ${msg.content.substring(0, 100)}...\n`
        })
        fullPrompt += '\n'
      }
      
      if (context) {
        fullPrompt += `当前上下文:\n${context}\n\n`
      }
      
      fullPrompt += `当前任务:\n${prompt}`

      // 使用流式响应
      const config = languageConfig
      const headers = { 'Content-Type': 'application/json' }
      let requestBody = {}

      if (config.model.includes('gemini')) {
        headers['x-goog-api-key'] = config.apiKey
        requestBody = {
          contents: [{ parts: [{ text: fullPrompt }] }],
          generationConfig: {
            temperature: 0.8,
            maxOutputTokens: maxTokens
          }
        }
      } else {
        headers['Authorization'] = `Bearer ${config.apiKey}`
        requestBody = {
          model: config.model,
          messages: [{ role: 'user', content: fullPrompt }],
          temperature: 0.8,
          max_tokens: maxTokens,
          stream: true
        }
      }

      const response = await fetch(config.apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        throw new Error(`API请求失败: ${response.status}`)
      }

      // 处理流式响应
      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let fullText = ''
      let buffer = ''
      let inAdvice = false
      let adviceText = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop()

        for (const line of lines) {
          if (line.trim() === '' || !line.startsWith('data: ')) continue
          
          const data = line.slice(6)
          if (data === '[DONE]') break

          try {
            const parsed = JSON.parse(data)
            let chunk = ''
            
            if (config.model.includes('gemini')) {
              chunk = parsed.candidates?.[0]?.content?.parts?.[0]?.text || ''
            } else {
              chunk = parsed.choices?.[0]?.delta?.content || ''
            }

            if (chunk) {
              fullText += chunk
              
              // 检测是否在专业建议区域内
              if (fullText.includes('===PROFESSIONAL_ADVICE_START===') && !inAdvice) {
                inAdvice = true
                adviceText = ''
              }
              
              if (inAdvice) {
                // 提取开始标记之后的内容
                const startIndex = fullText.indexOf('===PROFESSIONAL_ADVICE_START===')
                if (startIndex !== -1) {
                  let currentAdvice = fullText.substring(startIndex + '===PROFESSIONAL_ADVICE_START==='.length)
                  
                  // 检查是否包含结束标记
                  const endMarkerIndex = currentAdvice.indexOf('===PROFESSIONAL_ADVICE_END===')
                  
                  if (endMarkerIndex !== -1) {
                    // 找到结束标记，只取结束标记之前的内容
                    currentAdvice = currentAdvice.substring(0, endMarkerIndex)
                    
                    // 最后一次回调，显示完整内容
                    if (onAdviceChunk) {
                      let cleanAdvice = currentAdvice
                        .replace(/^=+/g, '')  // 去除开头的所有等号
                        .replace(/=+$/g, '')  // 去除结尾的所有等号
                        .replace(/={3,}/g, '') // 去除中间可能残留的连续等号
                        .trim()
                      if (cleanAdvice) {
                        onAdviceChunk(cleanAdvice)
                      }
                    }
                    
                    // 标记专业建议已结束
                    inAdvice = false
                  } else {
                    // 还没有结束标记，继续实时显示
                    // 先检查是否包含部分结束标记（避免闪现）
                    let displayAdvice = currentAdvice
                    
                    // 如果末尾包含可能是结束标记开头的等号，暂时不显示这部分
                    const trailingEquals = currentAdvice.match(/=+$/)
                    if (trailingEquals && trailingEquals[0].length >= 3) {
                      // 可能是结束标记的开始，先不显示这部分
                      displayAdvice = currentAdvice.substring(0, currentAdvice.length - trailingEquals[0].length)
                    }
                    
                    // 实时回调显示建议
                    if (onAdviceChunk) {
                      let cleanAdvice = displayAdvice
                        .replace(/^=+/g, '')  // 去除开头的所有等号
                        .replace(/=+$/g, '')  // 去除结尾的所有等号
                        .replace(/={3,}/g, '') // 去除中间可能残留的连续等号
                        .trim()
                      if (cleanAdvice) {
                        onAdviceChunk(cleanAdvice)
                      }
                    }
                  }
                }
              }
            }
          } catch (e) {
            // 忽略解析错误
          }
        }
      }

      console.log('✅ 流式AI响应完成')
      
      // 记录对话历史
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: prompt },
        { role: 'assistant', content: fullText }
      ])

      return fullText
    } catch (error) {
      console.error('❌ 流式AI调用失败:', error)
      toast.error('AI调用失败: ' + error.message)
      return null
    }
  }

  // 调用真实AI生成内容（非流式，用于备用）
  const callRealAI = async (prompt, context = '', stepNumber = null) => {
    try {
      const languageConfig = settingsService.getLanguageModelConfig()
      
      if (!languageConfig || !languageConfig.apiKey) {
        console.warn('⚠️ 未配置AI服务，使用备用方案')
        return null
      }

      const step = stepNumber || currentStep
      console.log('🤖 使用真实AI生成:', languageConfig.provider, languageConfig.model, '当前步骤:', step)

      let fullPrompt = '你是一位专业的互动故事创作顾问。\n\n'
      
      if (conversationHistory.length > 0) {
        fullPrompt += '之前的对话:\n'
        conversationHistory.forEach(msg => {
          fullPrompt += `${msg.role === 'user' ? '用户' : 'AI'}: ${msg.content.substring(0, 100)}...\n`
        })
        fullPrompt += '\n'
      }
      
      if (context) {
        fullPrompt += `当前上下文:\n${context}\n\n`
      }
      
      fullPrompt += `当前任务:\n${prompt}`

      // 根据步骤和模型动态调整maxTokens
      let maxTokens = 4096 // 默认值，兼容大多数模型
      
      // 根据模型类型调整token限制
      const modelName = languageConfig.model?.toLowerCase() || ''
      if (modelName.includes('gpt-4')) {
        // GPT-4 系列支持更大的输出
        maxTokens = step === 8 ? 8000 : 4096
      } else if (modelName.includes('deepseek')) {
        // DeepSeek 分镜设计使用8000，其他步骤使用4096
        maxTokens = step === 8 ? 8000 : 4096
      } else if (modelName.includes('claude')) {
        // Claude 支持较大输出
        maxTokens = step === 8 ? 8000 : 4096
      } else {
        // 其他模型使用保守策略
        maxTokens = step === 8 ? 4096 : 2048
      }
      
      console.log('📊 Token配置:', { step, model: modelName, maxTokens })
      
      const response = await RealAIService.callLanguageModel(fullPrompt, {
        temperature: 0.8,
        maxTokens: maxTokens,
        stream: false
      })

      if (!response) {
        throw new Error('AI服务返回空响应')
      }

      console.log('✅ AI响应成功')
      
      setConversationHistory(prev => [
        ...prev,
        { role: 'user', content: prompt },
        { role: 'assistant', content: response }
      ])

      return response
    } catch (error) {
      console.error('❌ AI调用失败:', error)
      toast.error('AI调用失败: ' + error.message)
      return null
    }
  }

  // 快速验证JSON基本结构
  const isValidJsonStructure = (jsonStr) => {
    const trimmed = jsonStr.trim()
    if (!trimmed) return false
    
    // 检查是否以有效的JSON开始和结束
    const isObject = trimmed.startsWith('{') && trimmed.endsWith('}')
    const isArray = trimmed.startsWith('[') && trimmed.endsWith(']')
    
    return isObject || isArray
  }

  // 解析AI返回的JSON - 使用终极解析器
  const parseAIResponse = (response, stepId) => {
    const isDev = process.env.NODE_ENV === 'development'
    
    if (isDev) {
      console.log('🚀 使用终极JSON解析器，步骤:', stepId)
      console.log('📄 响应长度:', response.length)
    }
    
    // 使用终极JSON解析器
    const result = UltimateJsonParser.parseAIResponse(response)
    
    if (result) {
      if (isDev) {
        console.log('🎉 终极解析器成功解析JSON')
        console.log('📊 解析结果类型:', Array.isArray(result) ? 'Array' : 'Object')
        console.log('📊 解析结果大小:', Array.isArray(result) ? result.length : Object.keys(result).length)
      }
      return result
    }
    
    // 如果终极解析器失败，回退到旧方法（作为备用）
    if (isDev) {
      console.log('⚠️ 终极解析器失败，尝试备用方法...')
    }
    
    try {
      // 备用方法：使用原有的AdvancedJsonRepair
      const fallbackResult = AdvancedJsonRepair.smartExtractAndRepair(response)
      if (fallbackResult) {
        if (isDev) {
          console.log('✅ 备用方法解析成功')
        }
        return fallbackResult
      }
    } catch (error) {
      console.error('❌ 备用方法也失败:', error.message)
    }
    
    console.error('❌ 所有解析方法都失败了')
    console.error('📄 响应前500字符:', response.substring(0, 500))
    return null
  }

  // JSON预处理函数
  const preprocessJsonString = (jsonStr, stepId) => {
    const isDev = process.env.NODE_ENV === 'development'
    let processed = jsonStr.trim()
    
    // 智能检测JSON开始位置（支持对象和数组）
    const firstBrace = processed.indexOf('{')
    const firstBracket = processed.indexOf('[')
    
    let jsonStart = -1
    let jsonType = ''
    
    // 选择更早出现的JSON开始标记
    if (firstBrace !== -1 && firstBracket !== -1) {
      if (firstBrace < firstBracket) {
        jsonStart = firstBrace
        jsonType = 'object'
      } else {
        jsonStart = firstBracket
        jsonType = 'array'
      }
    } else if (firstBrace !== -1) {
      jsonStart = firstBrace
      jsonType = 'object'
    } else if (firstBracket !== -1) {
      jsonStart = firstBracket
      jsonType = 'array'
    }
    
    // 移除JSON开始前的前缀
    if (jsonStart > 0) {
      if (isDev) {
        console.debug(`🧹 移除前缀，类型: ${jsonType}, 长度: ${jsonStart}`)
      }
      processed = processed.substring(jsonStart)
    }
    
    // 智能检测JSON结束位置
    let jsonEnd = -1
    if (jsonType === 'object') {
      jsonEnd = processed.lastIndexOf('}')
    } else if (jsonType === 'array') {
      jsonEnd = processed.lastIndexOf(']')
    }
    
    // 移除JSON结束后的后缀
    if (jsonEnd !== -1 && jsonEnd < processed.length - 1) {
      if (isDev) {
        console.debug('🧹 移除后缀，长度:', processed.length - jsonEnd - 1)
      }
      processed = processed.substring(0, jsonEnd + 1)
    }
    
    // 特殊处理分镜设计JSON
    if (stepId === 7) {
      processed = preprocessStoryboardJson(processed)
    }
    
    return processed
  }

  // 分镜设计JSON特殊预处理
  const preprocessStoryboardJson = (jsonStr) => {
    console.log('🎬 开始分镜JSON特殊预处理')
    
    // 检查是否包含shots数组
    if (!jsonStr.includes('"shots"')) {
      console.log('⚠️ 未找到shots字段，尝试修复')
      // 如果直接是数组格式，包装成对象
      if (jsonStr.startsWith('[') && jsonStr.endsWith(']')) {
        jsonStr = `{"shots": ${jsonStr}}`
        console.log('✅ 已包装为对象格式')
      }
    }
    
    // 检查JSON完整性
    const openBraces = (jsonStr.match(/\{/g) || []).length
    const closeBraces = (jsonStr.match(/\}/g) || []).length
    const openBrackets = (jsonStr.match(/\[/g) || []).length
    const closeBrackets = (jsonStr.match(/\]/g) || []).length
    
    console.log('🔍 括号匹配检查:', {
      braces: `${openBraces}/${closeBraces}`,
      brackets: `${openBrackets}/${closeBrackets}`
    })
    
    // 修复不匹配的括号
    if (openBraces > closeBraces) {
      const missingBraces = openBraces - closeBraces
      jsonStr += '}'.repeat(missingBraces)
      console.log('🔧 补充了', missingBraces, '个闭括号')
    }
    
    if (openBrackets > closeBrackets) {
      const missingBrackets = openBrackets - closeBrackets
      jsonStr += ']'.repeat(missingBrackets)
      console.log('🔧 补充了', missingBrackets, '个闭方括号')
    }
    
    // 检查shots数组是否完整
    const shotsMatch = jsonStr.match(/"shots"\s*:\s*\[([\s\S]*)\]/)
    if (shotsMatch) {
      const shotsContent = shotsMatch[1]
      const shotCount = (shotsContent.match(/"shotNumber"/g) || []).length
      console.log('📊 检测到', shotCount, '个shot对象')
      
      // 如果shot数量不足6个，尝试修复
      if (shotCount < 6) {
        console.log('⚠️ shot数量不足，尝试修复')
        // 这里可以添加更复杂的修复逻辑
      }
    }
    
    console.log('✅ 分镜JSON预处理完成')
    return jsonStr
  }
  
  // JSON清理辅助函数
  const cleanExtractedJson = (jsonStr) => {
    const isDev = process.env.NODE_ENV === 'development'
    let cleaned = jsonStr.trim()
    
    // 智能检测JSON开始位置（支持对象和数组）
    const firstBrace = cleaned.indexOf('{')
    const firstBracket = cleaned.indexOf('[')
    
    let jsonStart = -1
    let jsonType = ''
    
    // 选择更早出现的JSON开始标记
    if (firstBrace !== -1 && firstBracket !== -1) {
      if (firstBrace < firstBracket) {
        jsonStart = firstBrace
        jsonType = 'object'
      } else {
        jsonStart = firstBracket
        jsonType = 'array'
      }
    } else if (firstBrace !== -1) {
      jsonStart = firstBrace
      jsonType = 'object'
    } else if (firstBracket !== -1) {
      jsonStart = firstBracket
      jsonType = 'array'
    }
    
    // 移除JSON开始前的前缀
    if (jsonStart > 0) {
      if (isDev) {
        console.debug(`🧹 [清理] 移除前缀，类型: ${jsonType}, 长度: ${jsonStart}`)
      }
      cleaned = cleaned.substring(jsonStart)
    }
    
    // 智能检测JSON结束位置
    let jsonEnd = -1
    if (jsonType === 'object') {
      jsonEnd = cleaned.lastIndexOf('}')
    } else if (jsonType === 'array') {
      jsonEnd = cleaned.lastIndexOf(']')
    }
    
    // 移除JSON结束后的后缀
    if (jsonEnd !== -1 && jsonEnd < cleaned.length - 1) {
      if (isDev) {
        console.debug('🧹 [清理] 移除后缀，长度:', cleaned.length - jsonEnd - 1)
      }
      cleaned = cleaned.substring(0, jsonEnd + 1)
    }
    
    // 移除尾随逗号
    cleaned = cleaned.replace(/,(\s*[}\]])/g, '$1')
    
    // 确保大括号匹配
    const openBraces = (cleaned.match(/\{/g) || []).length
    const closeBraces = (cleaned.match(/\}/g) || []).length
    if (openBraces > closeBraces) {
      console.log('🧹 补充闭括号:', openBraces - closeBraces)
      cleaned += '}'.repeat(openBraces - closeBraces)
    }
    
    return cleaned
  }

  // 生成选项（步骤1-6）
  const generateOptionsForStep = async (step) => {
    setIsChatting(true)
    
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: `正在为你分析「${steps[step].name}」...`,
      timestamp: new Date()
    }])
    
    // 构建上下文
    const context = Object.keys(confirmedData).map(key => {
      const stepData = confirmedData[key]
      if (stepData && stepData.title) {
        return `${key}: ${stepData.title} - ${stepData.description}`
      }
      return `${key}: ${JSON.stringify(stepData)}`
    }).join('\n')

    // 构建提示词
    let prompt = ''
    
    if (step === 1) {
      prompt = `作为专业的互动故事创作顾问，请分析以下创意想法。

创意想法：${userDescription}

**第一步：专业建议**
请先针对这个具体想法，提供专业的互动内容设计建议（100-150字），请使用结构化格式：

===PROFESSIONAL_ADVICE_START===
1. 题材适配性分析
2. 互动性设计要点
3. 决策点设计思路
4. 情感联系建议
===PROFESSIONAL_ADVICE_END===

**第二步：题材推荐**
然后推荐3个最适合的题材类型：

**重要：请严格按照以下格式返回JSON，不要添加任何其他文字：**

===GENRE_OPTIONS_JSON_START===
[
  {"id": 1, "title": "题材名称", "description": "推荐理由（强调互动性）", "score": 9},
  {"id": 2, "title": "题材名称", "description": "推荐理由（强调情感调动）", "score": 8},
  {"id": 3, "title": "题材名称", "description": "推荐理由（强调决策设计）", "score": 7}
]
===GENRE_OPTIONS_JSON_END===

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确`
    } else if (step === 2) {
      prompt = `作为互动内容设计专家，请针对已选择的题材提供建议。

已选题材：${confirmedData.genre?.title || ''}
题材特点：${confirmedData.genre?.description || ''}

**第一步：专业建议**
针对这个题材，请提供具体的主题设计建议（100-150字），请使用结构化格式：

===PROFESSIONAL_ADVICE_START===
1. 主题情感共鸣点
2. 道德困境设计
3. 情绪调动技巧
4. 互动结合点
===PROFESSIONAL_ADVICE_END===

**第二步：主题推荐**
推荐3个深刻的主题方向：

**重要：请严格按照以下格式返回JSON，不要添加任何其他文字：**

===THEME_OPTIONS_JSON_START===
[
  {"id": 1, "title": "主题名称", "description": "主题描述（结合题材特点）", "score": 9},
  {"id": 2, "title": "主题名称", "description": "主题描述（强调情感冲突）", "score": 8},
  {"id": 3, "title": "主题名称", "description": "主题描述（强调价值选择）", "score": 7}
]
===THEME_OPTIONS_JSON_END===

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确`
    } else if (step === 3) {
      prompt = `作为视觉设计专家，请针对当前的题材和主题提供美术建议。

已选题材：${confirmedData.genre?.title || ''}
已选主题：${confirmedData.theme?.title || ''}

**第一步：专业建议**
针对这个题材和主题组合，请提供美术风格建议（100-150字），请使用结构化格式：

===PROFESSIONAL_ADVICE_START===
1. 情感基调匹配
2. 视觉互动设计
3. 色彩情绪影响
4. 成本平衡考虑
===PROFESSIONAL_ADVICE_END===

**第二步：美术风格推荐**
推荐3种适合的美术风格：

**重要：请严格按照以下格式返回JSON，不要添加任何其他文字：**

===STYLE_OPTIONS_JSON_START===
[
  {"id": 1, "title": "风格名称", "description": "风格特点（结合题材主题）", "score": 9},
  {"id": 2, "title": "风格名称", "description": "风格特点（强调情绪表达）", "score": 8},
  {"id": 3, "title": "风格名称", "description": "风格特点（强调用户体验）", "score": 7}
]
===STYLE_OPTIONS_JSON_END===

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确`
    } else if (step === 4) {
      prompt = `作为互动叙事专家，请针对当前的创作方向提供大纲建议。

已选题材：${confirmedData.genre?.title || ''}
已选主题：${confirmedData.theme?.title || ''}
美术风格：${confirmedData.style?.title || ''}

**第一步：专业建议**
针对这个组合，请提供互动故事大纲设计建议（150-200字），请使用结构化格式：

===PROFESSIONAL_ADVICE_START===
1. 关键分支节点
2. 情绪调动设计
3. 多结局结构
4. 世界观规则
5. 叙事陷阱避免
===PROFESSIONAL_ADVICE_END===

**第二步：多分支多结局大纲方案**
创作3个完整的多分支互动故事大纲方案：

【核心要求 - 多分支多结局设计】
1. 每个方案必须包含3-5个不同结局
2. 设计2-4个关键决策分支点
3. 明确的分支路径和合流逻辑
4. 每个选择要有情绪对立性（信任vs怀疑、愤怒vs冷静等）

**重要：请严格按照以下格式返回JSON，不要添加任何其他文字：**

===OUTLINE_OPTIONS_JSON_START===
[
  {
    "id": 1,
    "title": "大纲标题",
    "description": "核心剧情概述（100字内）",
    "protagonist": "主角设定",
    "conflict": "核心冲突",
    "turning": "关键转折",
    "branchStructure": "分支结构说明（如：M1→M2→M3(决策1)→[L1-1→L1-2→E1结局 / L2-1→L2-2→E2结局]）",
    "keyDecisions": [
      {
        "position": "决策点位置（第几个节点）",
        "question": "决策问题",
        "choiceA": "选择1（情绪类型：信任/冷静/希望）",
        "choiceB": "选择2（情绪类型：怀疑/愤怒/恐惧）",
        "impact": "选择影响"
      }
    ],
    "endings": [
      {
        "id": "E1",
        "name": "结局名称",
        "type": "Happy/Sad/Bittersweet/Neutral/Twist",
        "description": "结局描述",
        "triggerPath": "触发路径（M1→M2→M3→L1-1→L1-2→E1）"
      },
      {
        "id": "E2",
        "name": "结局名称",
        "type": "Sad/Bittersweet",
        "description": "结局描述",
        "triggerPath": "触发路径"
      },
      {
        "id": "E3",
        "name": "结局名称",
        "type": "Neutral",
        "description": "结局描述",
        "triggerPath": "触发路径"
      }
    ],
    "worldview": {
      "era": "时代背景",
      "society": "社会结构",
      "rules": "核心规则"
    },
    "estimatedNodes": 15,
    "score": 9
  },
  {
    "id": 2,
    "title": "大纲标题",
    "description": "核心剧情概述（100字内）",
    "protagonist": "主角设定",
    "conflict": "核心冲突",
    "turning": "关键转折",
    "branchStructure": "分支结构说明",
    "keyDecisions": [
      {
        "position": "决策点位置",
        "question": "决策问题",
        "choiceA": "选择A",
        "choiceB": "选择B",
        "impact": "选择影响"
      }
    ],
    "endings": [
      {
        "id": "E1",
        "name": "结局名称",
        "type": "Happy",
        "description": "结局描述",
        "triggerPath": "触发路径"
      },
      {
        "id": "E2",
        "name": "结局名称",
        "type": "Sad",
        "description": "结局描述",
        "triggerPath": "触发路径"
      }
    ],
    "worldview": {
      "era": "时代背景",
      "society": "社会结构",
      "rules": "核心规则"
    },
    "estimatedNodes": 12,
    "score": 8
  },
  {
    "id": 3,
    "title": "大纲标题",
    "description": "核心剧情概述（100字内）",
    "protagonist": "主角设定",
    "conflict": "核心冲突",
    "turning": "关键转折",
    "branchStructure": "分支结构说明",
    "keyDecisions": [
      {
        "position": "决策点位置",
        "question": "决策问题",
        "choiceA": "选择A",
        "choiceB": "选择B",
        "impact": "选择影响"
      }
    ],
    "endings": [
      {
        "id": "E1",
        "name": "结局名称",
        "type": "Bittersweet",
        "description": "结局描述",
        "triggerPath": "触发路径"
      },
      {
        "id": "E2",
        "name": "结局名称",
        "type": "Neutral",
        "description": "结局描述",
        "triggerPath": "触发路径"
      },
      {
        "id": "E3",
        "name": "结局名称",
        "type": "Twist",
        "description": "结局描述",
        "triggerPath": "触发路径"
      }
    ],
    "worldview": {
      "era": "时代背景",
      "society": "社会结构",
      "rules": "核心规则"
    },
    "estimatedNodes": 18,
    "score": 7
  }
]
===OUTLINE_OPTIONS_JSON_END===

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确`
    }

    // 创建专业建议消息占位
    const adviceMessageId = Date.now()
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isAdvice: true,
      id: adviceMessageId,
      isTyping: true
    }])
    
    // 使用流式调用AI，实时显示专业建议
    const aiResponse = await callRealAIStreaming(prompt, context, (adviceChunk) => {
      // 实时更新专业建议
      setChatMessages(prev => prev.map(msg => 
        msg.id === adviceMessageId ? { ...msg, content: adviceChunk } : msg
      ))
    })
    
    let options = []
    
    if (aiResponse) {
      // 流式回复完成后立即移除typing状态
      setChatMessages(prev => prev.map(msg => 
        msg.id === adviceMessageId ? { ...msg, isTyping: false } : msg
      ))
      
      // 立即添加分析状态消息
      const stepNames = {
        1: '题材',
        2: '主题', 
        3: '美术风格',
        4: '故事大纲'
      }
      
      const analysisMessageId = Date.now()
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `正在为你分析${stepNames[step]}选项...`,
        timestamp: new Date(),
        isAdvice: true,
        id: analysisMessageId,
        isTyping: true
      }])
      
      // 短暂停顿
      await new Promise(resolve => setTimeout(resolve, 500))
      
      // 提取选项
      const parsed = parseAIResponse(aiResponse, step)
      if (parsed && Array.isArray(parsed)) {
        options = parsed.map((opt, idx) => ({
          id: opt.id || idx + 1,
          title: opt.title || opt.name || `选项${idx + 1}`,
          description: opt.description || opt.desc || '',
          score: opt.score || 8,
          ...opt // 保留其他字段（如 protagonist, conflict 等）
        }))
        
        // 移除分析状态消息
        setChatMessages(prev => prev.filter(msg => msg.id !== analysisMessageId))
      } else {
        // 解析失败，移除分析状态消息
        setChatMessages(prev => prev.filter(msg => msg.id !== analysisMessageId))
      }
    } else {
      // AI调用失败，移除空的建议消息
      setChatMessages(prev => prev.filter(msg => msg.id !== adviceMessageId))
    }
    
    // 如果AI生成失败或没有返回有效数据，使用备用选项
    if (options.length === 0) {
      console.log('使用备用选项')
      if (step === 1) {
        options = [
          { id: 1, title: '科幻题材', description: '探索未来科技与人性的碰撞', score: 9 },
          { id: 2, title: '奇幻冒险', description: '魔法世界中的英雄之旅', score: 8 },
          { id: 3, title: '悬疑推理', description: '层层谜团，抽丝剥茧', score: 7 }
        ]
      } else if (step === 2) {
        options = [
          { id: 1, title: '记忆与身份', description: '探讨记忆对身份认知的影响', score: 9 },
          { id: 2, title: '自由与控制', description: '个人自由与社会控制的冲突', score: 8 },
          { id: 3, title: '真相与欺骗', description: '追寻隐藏在表象下的真相', score: 8 }
        ]
      } else if (step === 3) {
        options = [
          { id: 1, title: '赛博朋克', description: '霓虹闪烁的未来都市，高科技低生活', score: 9 },
          { id: 2, title: '蒸汽朋克', description: '维多利亚时代的机械美学', score: 7 },
          { id: 3, title: '极简未来', description: '简洁冷峻的科幻风格', score: 8 }
        ]
      } else if (step === 4) {
        options = [
          { 
            id: 1, 
            title: '记忆追踪者', 
            description: '主角是一名记忆猎人，专门追踪被盗的记忆。某天接到一个委托，却发现这段记忆关乎自己的过去...', 
            score: 9 
          },
          { 
            id: 2, 
            title: '虚拟迷局', 
            description: '在虚拟世界中，玩家发现自己无法登出，必须完成一系列任务才能回到现实...', 
            score: 8 
          },
          { 
            id: 3, 
            title: '记忆商人', 
            description: '主角经营一家记忆交易所，某天收到一段神秘记忆，打开了潘多拉魔盒...', 
            score: 8 
          }
        ]
      }
    }
    
    setCurrentOptions(options)
    setSelectedOption(null)
    
    // 显示选项提示
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: `为你推荐了 ${options.length} 个${steps[step].name}选项，请选择一个继续：`,
      timestamp: new Date()
    }])
    
    setIsChatting(false)
  }

  // 备用内容数据
  const getFallbackContentForStep = (step) => {
    if (step === 5) {
      return {
        type: 'characters',
        title: '角色设计方案',
        characters: [
          {
            id: 'char-1',
            name: '主角',
            type: '主角',
            description: '故事的主人公',
            personality: '勇敢、坚定',
            imagePrompt: 'protagonist, hero, brave character',
            generatedImage: null,
            voiceType: '成熟声音',
            selectedVoice: null,
            voiceOptions: [
              { id: 'voice-1', name: '配音A', preview: 'mock-audio.mp3' },
              { id: 'voice-2', name: '配音B', preview: 'mock-audio.mp3' }
            ]
          }
        ]
      }
    } else if (step === 6) {
      return {
        type: 'scenes',
        title: '场景设计方案',
        scenes: [
          {
            id: 'scene-1',
            name: '起始场景',
            description: '故事开始的地方',
            atmosphere: '神秘',
            imagePrompt: 'starting scene, mysterious atmosphere',
            generatedImage: null,
            musicStyle: '环境音乐',
            generatedMusic: null,
            soundEffects: '环境音效',
            musicOptions: [
              { id: 'music-1', name: '背景音乐A', style: '环境', preview: 'mock-music.mp3' }
            ]
          }
        ]
      }
    } else if (step === 7) {
      return {
        type: 'storyboard',
        title: '分镜设计方案',
        shots: Array.from({ length: 15 }, (_, i) => ({
          shotNumber: i + 1,
          chapter: Math.floor(i / 5) + 1,
          scene: (i % 5) + 1,
          sceneId: `scene-${i + 1}`,
          sceneName: `场景${i + 1}`,
          title: `镜头${i + 1}`,
          description: `镜头${i + 1}的描述`,
          content: `镜头${i + 1}的详细内容`,
          type: i === 14 ? 'ending' : 'normal',
          imagePrompt: `shot ${i + 1}, detailed scene`,
          generatedImage: '',
          sceneImage: '',
          backgroundImage: '',
          imageFixed: false,
          imageSource: 'none',
          cameraAngle: '中景',
          duration: '5秒',
          location: '场景地点',
          timeOfDay: '白天',
          mood: '平静',
          background: '背景描述',
          musicStyle: '背景音乐',
          characters: [],
          selectedCharacters: [],
          dialogues: [],
          hasBranch: false,
          isBranch: false,
          branches: [],
          choices: [],
          isEnding: i === 14,
          endingType: i === 14 ? '结局' : '',
          endingDescription: i === 14 ? '故事结局' : ''
        }))
      }
    }
    return null
  }

  // 生成详细内容（步骤5-7）
  const generateContentForStep = async (step) => {
    setIsChatting(true)
    
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: `正在基于大纲为你设计「${steps[step].name}」...`,
      timestamp: new Date()
    }])
    
    // 构建上下文
    const context = Object.keys(confirmedData).map(key => {
      const stepData = confirmedData[key]
      if (stepData && stepData.title) {
        return `${key}: ${stepData.title} - ${stepData.description}`
      }
      return `${key}: ${JSON.stringify(stepData)}`
    }).join('\n')
    
    let generatedContent = null
    let prompt = ''
    
    if (step === 5) {
      // 角色设计 - 使用真实AI
      prompt = `作为互动内容创作专家，请基于前面确定的故事大纲，设计3-5个主要角色。

**要求：**
1. 角色设计要服务于互动故事，具有行动动机和成长空间
2. 提供完整的角色信息，包括外貌、性格、背景故事
3. 提供清晰的英文文生图关键词
4. 严格按照JSON格式返回，放在指定标记之间

**重要：请严格按照以下格式返回JSON，不要添加任何其他文字：**

===CHARACTERS_JSON_START===
{
  "characters": [
    {
      "id": "char-1",
      "name": "角色名称",
      "role": "main",
      "type": "主角",
      "description": "角色简要描述",
      "appearance": "外貌描述，包括身高、体型、发型、服饰等",
      "personality": "性格特点，如勇敢、冷静、机智等",
      "background": "背景故事，包括出身、经历、动机等",
      "imagePrompt": "character portrait, detailed appearance description in English, facial features, clothing style, lighting",
      "avatar": "",
      "voiceType": "成熟男声"
    },
    {
      "id": "char-2",
      "name": "角色名称",
      "role": "supporting",
      "type": "配角",
      "description": "角色简要描述",
      "appearance": "外貌描述",
      "personality": "性格特点",
      "background": "背景故事",
      "imagePrompt": "character portrait, detailed appearance description in English",
      "avatar": "",
      "voiceType": "成熟女声"
    },
    {
      "id": "char-3",
      "name": "角色名称",
      "role": "npc",
      "type": "反派",
      "description": "角色简要描述",
      "appearance": "外貌描述",
      "personality": "性格特点",
      "background": "背景故事",
      "imagePrompt": "character portrait, detailed appearance description in English",
      "avatar": "",
      "voiceType": "深沉男声"
    }
  ]
}
===CHARACTERS_JSON_END===

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确`

      const aiResponse = await callRealAI(prompt, context, step)
      
      if (aiResponse) {
        const parsed = parseAIResponse(aiResponse, step)
        if (parsed && parsed.characters) {
          generatedContent = {
            type: 'characters',
            title: '角色设计方案',
            characters: parsed.characters.map(char => ({
              ...char,
              generatedImage: null,
              selectedVoice: null,
              voiceOptions: [
                { id: `voice-${char.id}-1`, name: `配音A - ${char.voiceType}风格1`, preview: 'mock-audio.mp3' },
                { id: `voice-${char.id}-2`, name: `配音B - ${char.voiceType}风格2`, preview: 'mock-audio.mp3' },
                { id: `voice-${char.id}-3`, name: `配音C - ${char.voiceType}风格3`, preview: 'mock-audio.mp3' }
              ]
            }))
          }
        }
      }
    } else if (step === 6) {
      // 场景设计 - 使用真实AI
      prompt = `作为互动内容创作专家，请基于故事大纲和角色设计，设计5-8个主要场景地点。

**要求：**
1. 场景设计要服务于故事情节展开和角色互动
2. 提供氛围、音乐、音效描述
3. 提供清晰的英文文生图关键词
4. 严格按照JSON格式返回，放在指定标记之间

**重要：请严格按照以下格式返回JSON，不要添加任何其他文字：**

===SCENES_JSON_START===
{
  "scenes": [
    {
      "id": "scene-1",
      "name": "场景名称",
      "description": "镜头描述",
      "atmosphere": "紧张",
      "imagePrompt": "shot background, detailed description in English",
      "musicStyle": "紧张的背景音乐",
      "soundEffects": "环境音效描述"
    },
    {
      "id": "scene-2",
      "name": "场景名称",
      "description": "镜头描述",
      "atmosphere": "神秘",
      "imagePrompt": "shot background, detailed description in English",
      "musicStyle": "神秘的背景音乐",
      "soundEffects": "环境音效描述"
    },
    {
      "id": "scene-3",
      "name": "场景名称",
      "description": "镜头描述",
      "atmosphere": "温馨",
      "imagePrompt": "shot background, detailed description in English",
      "musicStyle": "温馨的背景音乐",
      "soundEffects": "环境音效描述"
    }
  ]
}
===SCENES_JSON_END===

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确`

      const aiResponse = await callRealAI(prompt, context, step)
      
      if (aiResponse) {
        const parsed = parseAIResponse(aiResponse, step)
        if (parsed && parsed.scenes) {
          generatedContent = {
            type: 'scenes',
            title: '镜头设计方案',
            scenes: parsed.scenes.map(scene => ({
              ...scene,
              generatedImage: null,
              generatedMusic: null,
              musicOptions: [
                { id: `music-${scene.id}-1`, name: `${scene.musicStyle} 风格1`, style: scene.musicStyle, preview: 'mock-music.mp3' },
                { id: `music-${scene.id}-2`, name: `${scene.musicStyle} 风格2`, style: scene.musicStyle, preview: 'mock-music.mp3' },
                { id: `music-${scene.id}-3`, name: `${scene.musicStyle} 风格3`, style: scene.musicStyle, preview: 'mock-music.mp3' }
              ]
            }))
          }
        }
      }
    } else if (step === 7) {
      // 分镜设计 - 多分支多结局优化版
      const outlineData = confirmedData.outline || {}
      const branchStructure = outlineData.branchStructure || ''
      const endings = outlineData.endings || []
      const keyDecisions = outlineData.keyDecisions || []
      
      prompt = `基于前面的多分支多结局故事大纲，生成完整的分镜流程JSON数据。

【统一输出要求】
- 所有文本必须为中文表达（仅 imagePrompt 字段可使用英文关键词）。

【已确定的分支结构】
${branchStructure}

【已设计的结局（${endings.length}个）】
${endings.map((e, i) => `${i+1}. ${e.name} (${e.type}) - ${e.description}`).join('\n')}

【关键决策点（${keyDecisions.length}个）】
${keyDecisions.map((d, i) => `${i+1}. ${d.question}\n   A: ${d.choiceA}\n   B: ${d.choiceB}`).join('\n')}

【⚠️ 重要要求 - 多分支多结局（硬性约束，必须严格遵守）】
1. ⚠️ 每个决策点"严格只有2个选项"，不能多也不能少！
2. ⚠️ 必须至少生成2个决策节点（通常位于M3和M5位置），每个决策节点必须有choices数组，严格包含2个选项
3. ⚠️ 必须至少生成3-5个不同的结局节点（E1, E2, E3, E4, E5...），每个结局节点必须 isEnding: true
4. 在 M 系列上的决策点：
   - 选项A：继续 M 系列（M{k+1}→...→E1）。
   - 选项B：新起一条顶层 L 支线（L{n}-1→L{n}-2→...→E{n+1}）。
5. 在 L 系列上的决策点（如 Lx-y）：
   - 选项A：继续当前 Lx 支线（Lx-{y+1}→...→E{x+1}）。
   - 选项B：再新起一条"顶层" L 支线（Ly-1→...→E{y+1}），或根据剧情需要设计"可合流到后续主线 M{t}"的分支（请显式给出 mergeTarget: "M{t}"）。
6. "一个选项只能连接一个 nextScene"，不允许一个选项指向多条线路。
7. 结局归属：E1 为主线结局，E{n>1} 为 L{n-1} 支线结局；所有结局节点必须 isEnding: true。
8. ⚠️ 覆盖完整剧情：主线至少5-8个 M 节点；每条 L 支线3-5个节点后进入对应结局或合流；总节点数必须在15-25之间。
9. ⚠️ JSON必须完整闭合，不要截断！如果内容太长，请确保至少包含15个节点和3个结局！
10. ⚠️ 禁止生成线性故事！必须包含分支和多个结局！

【关键分支逻辑要求 - 线性支线命名法】
- 每个分支选择都必须有对应的支线故事节点（L1-1, L1-2, L1-3... 或 L2-1, L2-2, L2-3...）。
- 允许的分支形态（请随机混用以提升可玩性）：
  1) 双分支：一条继续本线至结局；一条新起支线至结局。
  2) 支线再分支：支线中再次出现2选项，一条延续当前支线至结局；一条新起支线至结局。
  3) 可合流支线：支线后续在合流点与主线合并，共用主线后续与结局（请标注 mergeTarget）。
- 确保每个分支路径都有完整的剧情发展，不能出现断点
- 所有分支最终都必须有明确的结局或合并点

【节点类型说明】
- type: "start" - 开始节点（第1个节点）
- type: "normal" - 普通剧情节点
- type: "decision" - 决策分支节点（hasBranch: true，必须有choices数组）
- type: "merge" - 合流节点
- type: "ending" - 结局节点（isEnding: true，需设置endingType）

【JSON格式要求】
===STORYBOARD_JSON_START===
{
  "shots": [
    {
      "shotNumber": 1,
      "chapter": 1,
      "scene": 1,
      "sceneId": "M1",
      "sceneName": "开场",
      "title": "故事开始",
      "description": "开场描述",
      "content": "开场详细内容",
      "type": "start",
      "imagePrompt": "opening scene in English",
      "generatedImage": "",
      "sceneImage": "",
      "backgroundImage": "",
      "imageFixed": false,
      "imageSource": "none",
      "cameraAngle": "全景",
      "duration": "5秒",
      "location": "开场地点",
      "timeOfDay": "白天",
      "mood": "神秘",
      "background": "背景描述",
      "musicStyle": "开场音乐",
      "characters": [],
      "selectedCharacters": [],
      "dialogues": [],
      "hasBranch": false,
      "isBranch": false,
      "branches": [],
      "choices": [],
      "isEnding": false,
      "endingType": "",
      "endingDescription": "",
      "nodeType": "start",
      "branchBelong": "main"
    },
    {
      "shotNumber": 3,
      "chapter": 1,
      "scene": 3,
      "sceneId": "M3",
      "sceneName": "第一次抉择",
      "title": "关键决策",
      "description": "决策场景描述",
      "content": "决策场景详细内容",
      "type": "decision",
      "imagePrompt": "decision point, tense in English",
      "generatedImage": "",
      "sceneImage": "",
      "backgroundImage": "",
      "imageFixed": false,
      "imageSource": "none",
      "cameraAngle": "中景",
      "duration": "8秒",
      "location": "决策地点",
      "timeOfDay": "夜晚",
      "mood": "紧张",
      "background": "紧张氛围",
      "musicStyle": "悬疑音乐",
      "characters": [],
      "selectedCharacters": [],
      "dialogues": [],
      "hasBranch": true,
      "isBranch": true,
      "branches": ["M4", "L1-1"],
      "choices": [
        {
          "id": "choice-M3-1",
          "text": "继续主线剧情（选项A：继续M系列）",
          "nextScene": "M4",
          "targetNodeId": "M4",
          "targetSceneId": "M4",
          "emotionalType": "determined",
          "consequence": "继续沿着主线发展，揭开真相"
        },
        {
          "id": "choice-M3-2",
          "text": "选择支线剧情（选项B：新起L1支线）",
          "nextScene": "L1-1",
          "targetNodeId": "L1-1",
          "targetSceneId": "L1-1",
          "emotionalType": "trust",
          "consequence": "进入L1支线剧情，发现不同的故事线"
        }
      ],
      "isEnding": false,
      "endingType": "",
      "endingDescription": "",
      "nodeType": "decision",
      "branchBelong": "main"
    },
    {
      "shotNumber": 15,
      "chapter": 2,
      "scene": 8,
      "sceneId": "E1",
      "sceneName": "圆满结局",
      "title": "结局1",
      "description": "结局描述",
      "content": "结局详细内容",
      "type": "ending",
      "imagePrompt": "happy ending in English",
      "generatedImage": "",
      "sceneImage": "",
      "backgroundImage": "",
      "imageFixed": false,
      "imageSource": "none",
      "cameraAngle": "远景",
      "duration": "10秒",
      "location": "结局地点",
      "timeOfDay": "黎明",
      "mood": "温馨",
      "background": "温暖场景",
      "musicStyle": "温暖主题曲",
      "characters": [],
      "selectedCharacters": [],
      "dialogues": [],
      "hasBranch": false,
      "isBranch": false,
      "branches": [],
      "choices": [],
      "isEnding": true,
      "endingType": "Happy",
      "endingDescription": "【圆满结局】详细结局描述",
      "nodeType": "ending",
      "branchBelong": "endingA"
    }
  ]
}
===STORYBOARD_JSON_END===

【⚠️ 生成要求 - 必须严格遵守，违反将被拒绝】
- ⚠️ 必须生成15-25个完整的shot对象，不能少于15个！
- ⚠️ 必须至少包含2个决策节点（type="decision", hasBranch=true, isBranch=true），每个决策节点必须有choices数组，严格包含2个选项
- ⚠️ 必须至少包含3-5个结局节点（type="ending", isEnding=true），分别对应不同的结局类型
- 开始节点：shotNumber=1, type="start", sceneId="M1"
- 主线节点：M2, M3, M4, M5, M6... 至少5-8个M节点（必须有决策节点在其中）
- 每个决策节点必须有choices数组，严格只有2项，每个choice必须有nextScene字段指向目标节点
- 支线节点：L1-1, L1-2, L1-3, L2-1, L2-2... 每个支线至少3-5个节点
- 结局节点：至少${Math.max(3, endings.length)}个, type="ending", isEnding=true, endingType需对应已设计结局
- sceneId必须遵循命名规范（M1,M2,L1-1,L2-1,E1,E2,E3...）
- ⚠️ JSON必须完整闭合，最后是 }]}，不允许截断！
- shotNumber必须从1开始连续递增
- ⚠️ 禁止生成只有7个镜头的线性故事！必须包含分支和多个结局！

【关键约束 - 必须满足】：
1. 至少要有2个决策节点（通常在M3和M5位置）
2. ⚠️ 每个决策节点的choices数组必须"严格只有2项"，不能多也不能少！
3. ⚠️ 禁止在同一个决策点设置3个或更多选项，这会破坏故事体验！
4. M系列决策的两个选项：
   - 第1个选项的nextScene必须指向下一个M节点（如M3的选项1指向M4）
   - 第2个选项的nextScene必须指向新L支线起点（如M3的选项2指向L1-1）
5. L系列决策的两个选项：
   - 第1个选项的nextScene指向当前支线的下一个节点（如L1-2的选项1指向L1-3）
   - 第2个选项的nextScene指向新的顶层L支线起点（如L1-2的选项2指向L3-1，注意是L3不是L1-2_2！）
6. 每条L支线必须有独立的结局节点
7. 所有节点必须通过choices或线性连接形成完整路径
8. ⚠️ 重要：每个决策点只设计2个有意义的剧情选择，不要为了增加分支而强行添加第3个选项
9. ⚠️ 禁止一个分支连接多个结局！每个支线只能有一个结局节点
10. ⚠️ 如果节点有choices（决策点），则不应该有后续的线性连接
11. ⚠️ 结局节点必须是终点，不能有任何后续节点或choices
12. ⚠️ **禁止生成孤立支线！** 每个L支线（L1, L2, L3...）都必须有对应的决策点指向它的起点（L1-1, L2-1, L3-1...）
13. ⚠️ **支线数量 = 决策点数量！** 如果有3个决策节点，最多只能有3个L支线（L1, L2, L3）
14. ⚠️ **结局节点命名规范！** 结局节点必须严格使用 E1, E2, E3, E4, E5... 的格式
    - E1 是主线结局（M系列的结局）
    - E2 是L1支线的结局
    - E3 是L2支线的结局
    - E4 是L3支线的结局
    - 以此类推
15. ⚠️ **禁止使用其他结局命名！** 不要使用 L1-3, L2-2, M8 等作为结局节点，这些应该是剧情节点，不是结局！
16. ⚠️ **节点命名规范！** 严格遵守以下命名格式：
    - M系列：M1, M2, M3, M4, M5... （不要用 M1_2, M2-a 等）
    - L系列：L1-1, L1-2, L1-3, L2-1, L2-2, L3-1... （不要用 L1-2_2, L1-2-a, L1_2 等）
    - E系列：E1, E2, E3, E4, E5... （不要用 E1-1, E_1, EL1 等）
    - ❌ 错误命名：L1-2_2, L2-3_2, M5-a, E1-1
    - ✅ 正确命名：L3-1, L4-1, M6, E4
- 确保所有分支路径都有对应的节点，不要遗漏任何结局
- 重要：每个choices中的nextScene必须指向实际存在的sceneId，确保分支连接完整
- 分支节点必须包含完整的后续剧情节点，直到对应的结局节点

【错误示例1 - 错误的节点命名】❌
{
  "sceneId": "L1-2",
  "type": "decision",
  "choices": [
    {"text": "继续当前支线", "nextScene": "L1-3"},
    {"text": "新起支线", "nextScene": "L1-2_2"}  // ❌ 错误！应该是 L3-1
  ]
}
// ❌ 问题：使用了 L1-2_2 这种不规范的命名，应该使用 L3-1

【错误示例2 - 一个分支连接多个结局】❌
{
  "sceneId": "L2-2",
  "type": "decision",
  "choices": [
    {"text": "立即回家", "nextScene": "E2"},
    {"text": "尝试挽回", "nextScene": "E4"}
  ]
}
// ❌ 问题：L2-2同时连接E2和E4两个结局，导致混乱

【正确示例 - L系列决策的正确命名】✅
{
  "sceneId": "L1-2",
  "type": "decision",
  "choices": [
    {"text": "继续当前支线", "nextScene": "L1-3"},  // ✅ 继续L1支线
    {"text": "新起支线", "nextScene": "L3-1"}       // ✅ 新起L3支线（不是L1-2_2！）
  ]
},
{
  "sceneId": "L1-3",
  "type": "normal",
  "content": "L1支线继续..."
},
{
  "sceneId": "E2",                                  // ⚠️ L1支线结局必须用E2
  "type": "ending",
  "isEnding": true,
  "title": "L1支线结局"
},
{
  "sceneId": "L3-1",                                // ✅ 新支线从L3-1开始
  "type": "normal",
  "content": "L3支线开始..."
},
{
  "sceneId": "L3-2",
  "type": "normal",
  "content": "L3支线继续..."
},
{
  "sceneId": "E4",                                  // ⚠️ L3支线结局必须用E4
  "type": "ending",
  "isEnding": true,
  "title": "L3支线结局"
}
// ✅ 正确：L1-2的第2个选项指向L3-1（新的顶层支线），不是L1-2_2！

【分支完整性检查要求】
- 每个分支选择都必须有至少2-3个后续支线节点
- 支线节点必须包含完整的剧情内容，不能只是占位符
- 如果支线要合并回主线，必须明确指定合并点（如A3→M5）
- 如果支线要产生独立结局，必须包含完整的支线结局节点
- 支线可以继续分支，但每个子分支也必须完整
- 所有分支路径都必须有明确的终点（结局或合并点）
- ⚠️ 一个支线只能有一个结局，不能同时连接多个结局节点

【关键要求 - 确保分支连接正确】
1. 每个choices中的nextScene必须指向实际存在的sceneId
2. 分支节点必须包含完整的后续剧情节点，直到对应的结局节点
3. 所有分支路径都必须有明确的终点（结局或合并点）
4. 分支选择必须连接到后续的支线故事节点
5. 结局节点必须正确标记为isEnding: true
6. 每个结局节点都必须有独特的endingType

【完整示例 - 包含主线和支线的正确结构】✅
{
  "shots": [
    {
      "shotNumber": 1,
      "sceneId": "M1",
      "type": "start",
      "title": "故事开始",
      "content": "故事的开端..."
    },
    {
      "shotNumber": 2,
      "sceneId": "M2",
      "type": "normal",
      "title": "主线场景2",
      "content": "剧情发展..."
    },
    {
      "shotNumber": 3,
      "sceneId": "M3",
      "type": "decision",
      "title": "第一个决策点",
      "hasBranch": true,
      "isBranch": true,
      "choices": [
        {
          "text": "继续主线剧情",
          "nextScene": "M4",              // ✅ 指向下一个M节点
          "targetNodeId": "M4"
        },
        {
          "text": "选择支线剧情",
          "nextScene": "L1-1",            // ✅ 指向L1支线起点
          "targetNodeId": "L1-1"
        }
      ]
    },
    {
      "shotNumber": 4,
      "sceneId": "M4",
      "type": "normal",
      "title": "主线场景4",
      "content": "主线继续..."
    },
    {
      "shotNumber": 5,
      "sceneId": "M5",
      "type": "normal",
      "title": "主线场景5",
      "content": "主线结尾..."
    },
    {
      "shotNumber": 6,
      "sceneId": "E1",                    // ⚠️ 主线结局必须用E1！
      "type": "ending",
      "title": "主线结局",
      "isEnding": true,
      "endingType": "Happy",
      "endingDescription": "主线的圆满结局"
    },
    {
      "shotNumber": 7,
      "sceneId": "L1-1",
      "type": "normal",
      "title": "L1支线场景1",
      "content": "支线剧情开始..."
    },
    {
      "shotNumber": 8,
      "sceneId": "L1-2",
      "type": "normal",
      "title": "L1支线场景2",
      "content": "支线剧情发展..."
    },
    {
      "shotNumber": 9,
      "sceneId": "L1-3",
      "type": "normal",
      "title": "L1支线场景3",
      "content": "支线剧情结尾..."
    },
    {
      "shotNumber": 10,
      "sceneId": "E2",                    // ⚠️ L1支线结局必须用E2！
      "type": "ending",
      "title": "L1支线结局",
      "isEnding": true,
      "endingType": "Bittersweet",
      "endingDescription": "L1支线的结局"
    }
  ]
}

⚠️ 关键提醒：
- M系列的结局必须是 E1
- L1支线的结局必须是 E2
- L2支线的结局必须是 E3
- L3支线的结局必须是 E4
- 绝对不要用 M6, L1-3, L2-4 等作为结局节点！

**JSON格式要求：**
1. 所有字段名和字符串值必须使用英文双引号(")，严禁使用中文引号（""''）
2. 中文内容可以直接输出，但必须在英文双引号内
3. 不要添加任何markdown标记、注释或其他说明文字
4. 确保JSON结构完整，括号配对正确，所有shot对象都必须包含完整的必需字段`

      const aiResponse = await callRealAI(prompt, context, step)
      
      if (aiResponse) {
        console.log('🎬 AI分镜响应长度:', aiResponse.length)
        console.log('🎬 AI分镜响应前500字符:', aiResponse.substring(0, 500))
        
        const parsed = parseAIResponse(aiResponse, step)
        console.log('🎬 解析后的分镜数据:', parsed)
        
        if (parsed && parsed.shots) {
          console.log('🎬 解析成功，分镜数量:', parsed.shots.length)
          console.log('🎬 分镜详情:', parsed.shots.map(s => ({
            shotNumber: s.shotNumber,
            sceneId: s.sceneId,
            type: s.type,
            hasBranch: s.hasBranch,
            isEnding: s.isEnding,
            choices: s.choices?.length || 0
          })))
          
          // 验证分镜是否符合要求
          const decisionNodes = parsed.shots.filter(s => s.type === 'decision' || s.hasBranch || (s.choices && s.choices.length > 0))
          const endingNodes = parsed.shots.filter(s => s.isEnding || s.type === 'ending' || /^E\d+$/.test(s.sceneId))
          
          console.log('📊 分镜验证统计:', {
            totalShots: parsed.shots.length,
            decisionNodes: decisionNodes.length,
            endingNodes: endingNodes.length,
            meetsRequirement: parsed.shots.length >= 15 && decisionNodes.length >= 2 && endingNodes.length >= 3
          })
          
          // 如果不符合要求，给出警告
          if (parsed.shots.length < 15) {
            console.warn('⚠️ 警告：分镜数量不足15个！当前:', parsed.shots.length)
            toast.error(`分镜数量不足（${parsed.shots.length}个），应该至少15个`, { duration: 5000 })
          }
          if (decisionNodes.length < 2) {
            console.warn('⚠️ 警告：决策节点不足2个！当前:', decisionNodes.length)
            toast.error(`决策节点不足（${decisionNodes.length}个），应该至少2个`, { duration: 5000 })
          }
          if (endingNodes.length < 3) {
            console.warn('⚠️ 警告：结局节点不足3个！当前:', endingNodes.length)
            toast.error(`结局节点不足（${endingNodes.length}个），应该至少3个`, { duration: 5000 })
          }
          
          // 验证和修复分支连接
          console.log('🔍 开始验证和修复分支连接...')
          const validatedShots = validateAndFixBranchConnections(parsed.shots)
          console.log('✅ 分支连接验证完成，修复后的分镜数量:', validatedShots.length)
          
          generatedContent = {
            type: 'storyboard',
            title: '分镜设计方案',
            shots: validatedShots.map(shot => ({
              ...shot,
              generatedImage: null
            }))
          }
        } else {
          console.warn('⚠️ 分镜解析失败或没有shots数据')
        }
      }
    }
    
    // 如果AI生成失败，使用备用数据
    if (!generatedContent) {
      console.warn('AI生成失败，使用备用数据')
      generatedContent = getFallbackContentForStep(step)
    }
    
    setCurrentGeneratedContent(generatedContent)
    
    setChatMessages(prev => [...prev, {
      role: 'assistant',
      content: `${steps[step].name}已完成！请查看左侧内容，确认后点击「继续」。`,
      timestamp: new Date()
    }])
    
    setIsChatting(false)
  }

  // 发送消息
  const sendMessage = async () => {
    if (!currentMessage.trim() || isChatting) return
    
    const userMsg = currentMessage
    setChatMessages(prev => [...prev, {
      role: 'user',
      content: userMsg,
      timestamp: new Date()
    }])
    
    setCurrentMessage('')
    
    // 如果是第一步，自动进入题材分析
    if (currentStep === 0) {
      setTimeout(() => {
        setCurrentStep(1)
        generateOptionsForStep(1)
      }, 500)
      return
    }
    
    // 如果用户在当前环节发送修改意见，重新生成
    if (steps[currentStep].needsChoice) {
      // 重新生成选项，带上用户的修改意见
      setIsChatting(true)
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `收到！我会根据你的意见重新生成「${steps[currentStep].name}」...`,
        timestamp: new Date()
      }])
      
      // 构建上下文，包含用户的修改意见
      const context = Object.keys(confirmedData).map(key => {
        const stepData = confirmedData[key]
        if (stepData && stepData.title) {
          return `${key}: ${stepData.title} - ${stepData.description}`
        }
        return `${key}: ${JSON.stringify(stepData)}`
      }).join('\n')
      
      const fullContext = `${context}\n\n用户修改意见：${userMsg}`
      
      // 重新生成当前步骤的内容
      if (steps[currentStep].needsChoice) {
        // 重新调用生成选项
        const aiResponse = await callRealAI(
          `基于用户的修改意见，请重新生成「${steps[currentStep].name}」的选项。`,
          fullContext
        )
        
        let options = []
        if (aiResponse) {
          const parsed = parseAIResponse(aiResponse, currentStep)
          if (parsed && Array.isArray(parsed)) {
            options = parsed.map((opt, idx) => ({
              id: opt.id || idx + 1,
              title: opt.title || opt.name || `选项${idx + 1}`,
              description: opt.description || opt.desc || '',
              score: opt.score || 8,
              ...opt // 保留其他字段
            }))
          }
        }
        
        if (options.length > 0) {
          setCurrentOptions(options)
          setSelectedOption(null)
          
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: `根据你的意见，我重新生成了 ${options.length} 个选项：`,
            timestamp: new Date()
          }])
        } else {
          setChatMessages(prev => [...prev, {
            role: 'assistant',
            content: '抱歉，重新生成失败。请再试一次或换个说法。',
            timestamp: new Date()
          }])
        }
      }
      
      setIsChatting(false)
    } else {
      // 对于角色、场景、分镜设计环节的修改
      toast.info('修改建议已记录，将在重新生成时考虑')
    }
  }

  // 选择选项
  const handleSelectOption = (option) => {
    setSelectedOption(option)
  }

  // 继续下一步
  const handleContinue = () => {
    if (!selectedOption && steps[currentStep].needsChoice) {
      toast.error('请先选择一个选项！')
      return
    }

    // 确认当前步骤的数据（使用深拷贝确保引用改变）
    if (steps[currentStep].needsChoice) {
      setConfirmedData(prev => {
        const newData = {
          ...prev,
          [steps[currentStep].key]: {
            ...selectedOption,
            __updatedAt: Date.now() // 添加时间戳确保引用改变
          }
        }
        console.log('✅ 已确认步骤数据:', steps[currentStep].key, newData[steps[currentStep].key])
        return newData
      })
    } else if (currentGeneratedContent) {
      setConfirmedData(prev => {
        const newData = {
          ...prev,
          [steps[currentStep].key]: {
            ...currentGeneratedContent,
            __updatedAt: Date.now() // 添加时间戳确保引用改变
          }
        }
        console.log('✅ 已确认生成内容:', steps[currentStep].key, newData[steps[currentStep].key])
        return newData
      })
    }

    // 强制触发UI更新
    setForceUpdate(prev => prev + 1)

    // 清空当前选项和生成内容
    setCurrentOptions([])
    setSelectedOption(null)
    setCurrentGeneratedContent(null)

    // 自动进入下一步
    const nextStep = currentStep + 1
    if (nextStep < steps.length) {
      setCurrentStep(nextStep)
      // 判断是生成选项还是生成详细内容
      if (steps[nextStep].needsChoice) {
        setTimeout(() => {
          generateOptionsForStep(nextStep)
        }, 500)
      } else {
        setTimeout(() => {
          generateContentForStep(nextStep)
        }, 500)
      }
    } else {
      // 所有步骤完成
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: '恭喜！所有创作环节已完成。请查看右侧的完整策划案，或点击右下角按钮进入编辑器。',
        timestamp: new Date()
      }])
    }
  }

  // 分支完整性验证函数
  const validateBranchIntegrity = (shots, connections) => {
    const errors = []
    const visitedNodes = new Set()
    const endingNodes = new Set()
    
    // 收集所有结局节点
    shots.forEach(shot => {
      if (shot.isEnding) {
        endingNodes.add(shot.sceneId || `scene-${shot.shotNumber}`)
      }
    })
    
    // 检查每个分支选择的目标节点
    shots.forEach(shot => {
      if (shot.choices && shot.choices.length > 0) {
        shot.choices.forEach((choice, index) => {
          const targetExists = shots.some(s => 
            s.sceneId === choice.nextScene || 
            `scene-${s.shotNumber}` === choice.nextScene
          )
          
          if (!targetExists) {
            errors.push(`分支选择 "${choice.text}" 指向不存在的节点: ${choice.nextScene}`)
          } else {
            // 检查分支路径是否完整
            const branchPath = findBranchPath(choice.nextScene, shots, connections)
            if (branchPath.length === 0) {
              errors.push(`分支选择 "${choice.text}" 的路径不完整，无法到达结局或合并点`)
            }
          }
        })
      }
    })
    
    // 检查是否有孤立的分支节点
    shots.forEach(shot => {
      const nodeId = shot.sceneId || `scene-${shot.shotNumber}`
      const hasIncoming = connections.some(conn => conn.to === nodeId)
      const hasOutgoing = connections.some(conn => conn.from === nodeId)
      
      if (!hasIncoming && nodeId !== 'M1' && nodeId !== 'scene-1') {
        errors.push(`节点 ${nodeId} 没有入边连接`)
      }
      
      if (!hasOutgoing && !shot.isEnding) {
        errors.push(`非结局节点 ${nodeId} 没有出边连接`)
      }
    })
    
    return {
      isValid: errors.length === 0,
      errors: errors
    }
  }
  
  // 查找分支路径的辅助函数
  const findBranchPath = (startNodeId, shots, connections) => {
    const path = []
    const visited = new Set()
    const queue = [startNodeId]
    
    while (queue.length > 0) {
      const currentNode = queue.shift()
      if (visited.has(currentNode)) continue
      
      visited.add(currentNode)
      path.push(currentNode)
      
      // 检查是否是结局节点
      const isEnding = shots.some(s => 
        (s.sceneId === currentNode || `scene-${s.shotNumber}` === currentNode) && s.isEnding
      )
      
      if (isEnding) {
        return path // 找到结局，路径完整
      }
      
      // 检查是否是合并点（支线回到主线）
      const isMergePoint = shots.some(s => 
        (s.sceneId === currentNode || `scene-${s.shotNumber}` === currentNode) && 
        s.sceneId && s.sceneId.startsWith('M')
      )
      
      if (isMergePoint && path.length > 1) {
        return path // 支线合并到主线，路径完整
      }
      
      // 查找下一个节点
      const nextConnections = connections.filter(conn => conn.from === currentNode)
      nextConnections.forEach(conn => {
        if (!visited.has(conn.to)) {
          queue.push(conn.to)
        }
      })
    }
    
    return path
  }
  
  // 检测支线合并逻辑
  const detectBranchMerge = (shots, connections) => {
    const mergePoints = []
    const branchPaths = new Map()
    
    // 分析每个分支路径
    shots.forEach(shot => {
      if (shot.choices && shot.choices.length > 0) {
        shot.choices.forEach(choice => {
          const branchPath = findBranchPath(choice.nextScene, shots, connections)
          if (branchPath.length > 0) {
            branchPaths.set(choice.nextScene, branchPath)
            
            // 检查路径中是否有主线节点（合并点）
            branchPath.forEach(nodeId => {
              const isMainLine = shots.some(s => 
                (s.sceneId === nodeId || `scene-${s.shotNumber}` === nodeId) && 
                s.sceneId && s.sceneId.startsWith('M')
              )
              
              if (isMainLine && !mergePoints.includes(nodeId)) {
                mergePoints.push({
                  nodeId: nodeId,
                  branchSource: choice.nextScene,
                  mergeType: '支线合并到主线'
                })
              }
            })
          }
        })
      }
    })
    
    return {
      mergePoints: mergePoints,
      branchPaths: branchPaths
    }
  }
  
  // 验证连接逻辑
  const validateConnections = (shots, connections) => {
    const errors = []
    
    // 检查结局节点是否有出边连接
    shots.forEach(shot => {
      if (shot.isEnding) {
        const nodeId = shot.sceneId || `scene-${shot.shotNumber}`
        const hasOutgoing = connections.some(conn => conn.from === nodeId)
        
        if (hasOutgoing) {
          errors.push(`结局节点 ${nodeId} 不应该有出边连接`)
        }
      }
    })
    
    // 检查分支节点是否有主输出端连接
    shots.forEach(shot => {
      if (shot.hasBranch || shot.choices) {
        const nodeId = shot.sceneId || `scene-${shot.shotNumber}`
        const hasMainOutput = connections.some(conn => 
          conn.from === nodeId && 
          (!conn.sourceHandle || conn.sourceHandle === 'output')
        )
        
        if (hasMainOutput) {
          errors.push(`分支节点 ${nodeId} 不应该有主输出端连接`)
        }
      }
    })
    
    // 检查分支选择是否都有对应的连接
    shots.forEach(shot => {
      if (shot.choices && shot.choices.length > 0) {
        shot.choices.forEach((choice, index) => {
          const sourceId = shot.sceneId || `scene-${shot.shotNumber}`
          const hasConnection = connections.some(conn => 
            conn.from === sourceId && 
            conn.sourceHandle === `choice-${index}`
          )
          
          if (!hasConnection) {
            errors.push(`分支选择 "${choice.text}" 没有对应的连接`)
          }
        })
      }
    })
    
    // 检查是否有重复的连接
    const connectionKeys = new Set()
    connections.forEach(conn => {
      const key = `${conn.from}-${conn.sourceHandle || 'output'}-${conn.to}-${conn.targetHandle || 'input'}`
      if (connectionKeys.has(key)) {
        errors.push(`重复连接: ${conn.from}(${conn.sourceHandle || 'output'}) → ${conn.to}(${conn.targetHandle || 'input'})`)
      }
      connectionKeys.add(key)
    })
    
    return {
      isValid: errors.length === 0,
      errors: errors
    }
  }
  
  // 生成节点句柄配置
  const generateNodeHandles = (shot) => {
    const handles = {
      // 输入端（所有节点都有）
      input: {
        id: 'input',
        type: 'target',
        position: 'left',
        style: { background: '#555' }
      }
    }
    
    // 分支节点：只有分支选项句柄，没有主输出端
    if (shot.hasBranch || shot.choices) {
      // 分支选项句柄（新格式）
      if (shot.choices && shot.choices.length > 0) {
        shot.choices.forEach((choice, index) => {
          handles[`choice-${index}`] = {
            id: `choice-${index}`,
            type: 'source',
            position: 'right',
            style: { background: '#8B5CF6' },
            label: choice.text || `选项${index + 1}`,
            data: {
              choiceIndex: index,
              emotionalType: choice.emotionalType,
              isBranchHandle: true
            }
          }
        })
      }
      
      // 旧格式分支句柄
      if (shot.branches && shot.branches.length > 0) {
        shot.branches.forEach((branch, index) => {
          handles[`branch-${index}`] = {
            id: `branch-${index}`,
            type: 'source',
            position: 'right',
            style: { background: '#8B5CF6' },
            label: branch.text || `选项${index + 1}`,
            data: {
              branchIndex: index,
              targetShotNumber: branch.targetShotNumber,
              isBranchHandle: true
            }
          }
        })
      }
    } else {
      // 普通节点：只有主输出端
      handles.output = {
        id: 'output',
        type: 'source',
        position: 'right',
        style: { background: '#555' }
      }
    }
    
    return handles
  }

  // 验证和修复分支连接
  const validateAndFixBranchConnections = (shots) => {
    console.log('🔍 开始验证分支连接，原始分镜数量:', shots.length)
    
    // 创建sceneId到shot的映射
    const sceneIdMap = new Map()
    shots.forEach(shot => {
      if (shot.sceneId) {
        sceneIdMap.set(shot.sceneId, shot)
      }
    })
    
    console.log('📊 可用的sceneId:', Array.from(sceneIdMap.keys()))
    
    const fixedShots = []
    const errors = []
    
    shots.forEach((shot, index) => {
      const fixedShot = { ...shot }
      
      // 修复分支选择的目标连接
      if (shot.choices && shot.choices.length > 0) {
        console.log(`🔧 修复Shot ${shot.sceneId} 的${shot.choices.length}个分支选择`)
        
        fixedShot.choices = shot.choices.map((choice, choiceIndex) => {
          const fixedChoice = { ...choice }
          
          // 检查目标节点是否存在
          if (!choice.nextScene || !sceneIdMap.has(choice.nextScene)) {
            console.warn(`⚠️ 分支选择 "${choice.text}" 的目标节点 ${choice.nextScene} 不存在`)
            
            // 尝试修复：查找相似名称的节点
            const possibleTargets = Array.from(sceneIdMap.keys()).filter(sceneId => 
              sceneId.includes(choice.nextScene) || choice.nextScene.includes(sceneId)
            )
            
            if (possibleTargets.length > 0) {
              fixedChoice.nextScene = possibleTargets[0]
              console.log(`✅ 修复分支选择目标: ${choice.nextScene} → ${possibleTargets[0]}`)
            } else {
              // 如果找不到目标，创建默认的支线节点
              const branchId = shot.sceneId.startsWith('M') ? 
                `A${shot.sceneId.slice(1)}` : 
                `${shot.sceneId}_${choiceIndex + 1}`
              
              fixedChoice.nextScene = branchId
              console.log(`🔧 创建默认分支目标: ${choice.nextScene} → ${branchId}`)
              
              // 添加缺失的分支节点
              if (!sceneIdMap.has(branchId)) {
                const branchShot = {
                  shotNumber: shots.length + fixedShots.length + 1,
                  sceneId: branchId,
                  sceneName: `分支${choiceIndex + 1}`,
                  title: `${choice.text}的后续`,
                  description: `选择"${choice.text}"后的剧情发展`,
                  content: `你选择了"${choice.text}"，这带来了新的发展...`,
                  type: 'normal',
                  hasBranch: false,
                  isBranch: false,
                  isEnding: false,
                  choices: [],
                  branches: [],
                  endingType: '',
                  endingDescription: '',
                  nodeType: 'normal',
                  branchBelong: shot.sceneId.startsWith('M') ? 'branchA' : 'branchB'
                }
                fixedShots.push(branchShot)
                sceneIdMap.set(branchId, branchShot)
              }
            }
          }
          
          return fixedChoice
        })
      }
      
      // 确保分支节点标记正确
      if (shot.choices && shot.choices.length > 0) {
        fixedShot.hasBranch = true
        fixedShot.isBranch = true
        fixedShot.type = 'decision'
      }
      
      // 确保结局节点标记正确
      if (shot.isEnding) {
        fixedShot.type = 'ending'
        fixedShot.hasBranch = false
        fixedShot.isBranch = false
        fixedShot.choices = []
        fixedShot.branches = []
        
        // 确保有结局类型
        if (!fixedShot.endingType) {
          fixedShot.endingType = shot.sceneId.includes('Happy') ? 'Happy' : 
                                shot.sceneId.includes('Sad') ? 'Sad' : 
                                shot.sceneId.includes('Neutral') ? 'Neutral' : 'Ending'
        }
      }
      
      fixedShots.push(fixedShot)
    })
    
    // 验证所有分支路径都有终点
    console.log('🔍 验证分支路径完整性...')
    const branchValidation = validateBranchPaths(fixedShots)
    if (!branchValidation.isValid) {
      console.warn('⚠️ 分支路径验证失败:', branchValidation.errors)
      errors.push(...branchValidation.errors)
    }
    
    console.log('✅ 分支连接修复完成，最终分镜数量:', fixedShots.length)
    if (errors.length > 0) {
      console.warn('⚠️ 修复过程中的警告:', errors)
    }
    
    return fixedShots
  }
  
  // 验证分支路径完整性
  const validateBranchPaths = (shots) => {
    const errors = []
    const sceneIdMap = new Map()
    shots.forEach(shot => {
      if (shot.sceneId) {
        sceneIdMap.set(shot.sceneId, shot)
      }
    })
    
    // 检查每个分支选择是否都能到达终点
    shots.forEach(shot => {
      if (shot.choices && shot.choices.length > 0) {
        shot.choices.forEach(choice => {
          const path = findPathToEnding(choice.nextScene, sceneIdMap, new Set())
          if (path.length === 0) {
            errors.push(`分支选择 "${choice.text}" 无法到达任何终点`)
          }
        })
      }
    })
    
    return {
      isValid: errors.length === 0,
      errors: errors
    }
  }
  
  // 查找从指定节点到终点的路径
  const findPathToEnding = (startSceneId, sceneIdMap, visited) => {
    if (visited.has(startSceneId)) return [] // 避免循环
    visited.add(startSceneId)
    
    const shot = sceneIdMap.get(startSceneId)
    if (!shot) return []
    
    if (shot.isEnding) return [startSceneId] // 找到终点
    
    // 如果有分支选择，检查每个选择
    if (shot.choices && shot.choices.length > 0) {
      for (const choice of shot.choices) {
        const subPath = findPathToEnding(choice.nextScene, sceneIdMap, new Set(visited))
        if (subPath.length > 0) {
          return [startSceneId, ...subPath]
        }
      }
    }
    
    return []
  }

// ⚠️ calculateNodePositions 已移至 src/utils/storyLayoutAlgorithm.js 统一管理
// 以下为旧代码，已废弃，请勿使用
const calculateNodePositions_DEPRECATED = (storyStructure) => {
  const nodePositions = new Map()
  const nodeRows = new Map()
  const nodeCols = new Map()
  const horizontalSpacing = 450
  const verticalSpacing = 300
  const baseX = 200
  const baseY = 150

  console.log('🎨 主线-分支层级布局算法（新版 - 与StoryEditor同步）...')

  // 找到开始节点
  const startNode = storyStructure.nodes.find(n => 
    !storyStructure.connections.some(c => c.to === n.id)
  ) || storyStructure.nodes[0]

  if (!startNode) {
    console.warn('⚠️ 未找到开始节点')
    return nodePositions
  }

  console.log('🎯 开始节点:', startNode.id)

  // ========== 数据结构定义 ==========
  const nodeBranch = new Map() // nodeId -> branchName (如: 'M', 'L1', 'L2', 'L3')
  const branchNodes = new Map() // branchName -> [nodeIds]
  const branchParent = new Map() // branchName -> parentBranchName
  const branchChildren = new Map() // branchName -> [childBranchNames]
  const branchAppearOrder = [] // 分支出现的顺序

  // ========== 第一步：BFS遍历，识别主线和所有分支 ==========
  console.log('\n📝 第一步：识别主线和分支结构...')
  
  const queue = [{ nodeId: startNode.id, row: 0, col: 0, branchName: 'M' }]
  const visited = new Set()
  let nextLBranchNumber = 1 // L分支计数器

  // 初始化M主线
  branchNodes.set('M', [])
  branchParent.set('M', null)
  branchChildren.set('M', [])
  branchAppearOrder.push('M')

    while (queue.length > 0) {
      const { nodeId, row, col, branchName } = queue.shift()

      if (visited.has(nodeId)) continue
      visited.add(nodeId)

      // 记录节点归属
      nodeBranch.set(nodeId, branchName)
      branchNodes.get(branchName).push(nodeId)

      // 记录基准位置
      nodeRows.set(nodeId, row)
      nodeCols.set(nodeId, col)

      console.log(`  ${nodeId}: baseRow=${row}, col=${col}, branch=${branchName}`)

      // 查找子节点
      const childConnections = storyStructure.connections.filter(c => c.from === nodeId)

      if (childConnections.length === 0) {
        // 叶子节点
        continue
      } else if (childConnections.length === 1) {
        // 单一连接，继续当前分支
        const childNodeId = childConnections[0].to
        if (!visited.has(childNodeId)) {
          queue.push({
            nodeId: childNodeId,
            row: row,
            col: col + 1,
            branchName: branchName
          })
        }
      } else {
        // 分支点
        console.log(`  🔀 分支点 ${nodeId} (分支: ${branchName})`)
        
        childConnections.forEach((conn, index) => {
          const childNodeId = conn.to
          if (visited.has(childNodeId)) return

          // 优先检查是否是M系列或结局节点
          const isMainNode = /^M\d+$/.test(childNodeId)
          const isEndingNode = /^E\d+$/.test(childNodeId)
          
          if (isMainNode && branchName === 'M') {
            // M系列节点：继续M主线
            queue.push({ nodeId: childNodeId, row: 0, col: col + 1, branchName: 'M' })
            console.log(`    ➡️ 继续M主线: ${childNodeId}`)
          } else if (isEndingNode) {
            // 结局节点：E1 属于主线，其余(E2+)属于对应的 L 分支
            const endingNum = parseInt(childNodeId.replace('E', ''))
            if (endingNum === 1 && branchName === 'M') {
              queue.push({ nodeId: childNodeId, row: 0, col: col + 1, branchName: 'M' })
              console.log('    🎬 主线结局 E1 放置在主线行 row 0')
            } else {
              let targetBranchName = branchName.startsWith('L') ? branchName : `L${Math.max(1, endingNum - 1)}`
              if (!branchNodes.has(targetBranchName)) {
                branchNodes.set(targetBranchName, [])
                branchParent.set(targetBranchName, 'M')
                if (!branchChildren.has('M')) branchChildren.set('M', [])
                branchChildren.get('M').push(targetBranchName)
                branchAppearOrder.push(targetBranchName)
                const lNumber = parseInt(targetBranchName.replace('L', ''))
                if (lNumber >= nextLBranchNumber) nextLBranchNumber = lNumber + 1
              }
              const newRow = branchName === 'M' ? 1 : row
              queue.push({ nodeId: childNodeId, row: newRow, col: col + 1, branchName: targetBranchName })
              console.log(`    🎬 分支结局 ${childNodeId} 归属 ${targetBranchName} 行(row=${newRow})`)
            }
          } else {
            // 检查子节点是否已经有明确的L系列归属（支持多级：L1-1, L2-1-1, L2-2-1等）
            // 匹配模式：L{n}-{m} 或 L{n}-{m}-{k}
            const lSeriesMatch = childNodeId.match(/^(L\d+)(?:-\d+)+$/)
            let targetBranchName = null
            
            if (lSeriesMatch) {
              // 子节点有L系列编号，提取顶级分支名（如 L1, L2, L3）
              const lSeriesName = lSeriesMatch[1] // 'L1', 'L2', 'L3'...
              
              // 查找是否已经有这个L系列分支
              if (branchNodes.has(lSeriesName)) {
                // 已存在，归属到现有分支
                targetBranchName = lSeriesName
                console.log(`    ↪️ 归属到现有分支 ${lSeriesName}: ${childNodeId}`)
              } else {
                // 不存在，创建新分支并用L系列名称
                targetBranchName = lSeriesName
                
                branchNodes.set(targetBranchName, [])
                branchParent.set(targetBranchName, branchName)
                if (!branchChildren.has(branchName)) {
                  branchChildren.set(branchName, [])
                }
                branchChildren.get(branchName).push(targetBranchName)
                branchAppearOrder.push(targetBranchName)
                
                // 更新L分支计数器
                const lNumber = parseInt(lSeriesName.replace('L', ''))
                if (lNumber >= nextLBranchNumber) {
                  nextLBranchNumber = lNumber + 1
                }
                
                console.log(`    🌿 创建新分支 ${targetBranchName} (基于节点ID, 父分支: ${branchName})`)
              }
            } else {
              // 子节点没有明确的L系列归属，创建新分支
              targetBranchName = `L${nextLBranchNumber}`
              nextLBranchNumber++

              branchNodes.set(targetBranchName, [])
              branchParent.set(targetBranchName, branchName)
              if (!branchChildren.has(branchName)) {
                branchChildren.set(branchName, [])
              }
              branchChildren.get(branchName).push(targetBranchName)
              branchAppearOrder.push(targetBranchName)
              
              console.log(`    🌿 创建新分支 ${targetBranchName} (父分支: ${branchName})`)
            }

            // 计算新节点的基准行号
            let newRow
            if (branchName === 'M') {
              newRow = 1 // 从M主线出来的所有分支初始都在row 1
            } else {
              // 从L分支出来的分支，在父分支基础上+1
              newRow = row + 1
            }

            queue.push({
              nodeId: childNodeId,
              row: newRow,
              col: col + 1,
              branchName: targetBranchName
            })
            console.log(`    → 节点 ${childNodeId} 加入分支 ${targetBranchName} (row=${newRow})`)
          }
        })
      }
    }

  console.log('\n📊 分支结构分析:')
  console.log('  分支出现顺序:', branchAppearOrder)
  for (const [branchName, nodes] of branchNodes.entries()) {
    const parent = branchParent.get(branchName)
    const children = branchChildren.get(branchName) || []
    console.log(`  ${branchName}: ${nodes.length}个节点, 父分支=${parent}, 子分支=[${children.join(', ')}]`)
  }

  // ========== 第二步：优先行规则 + 碰撞检测 ==========
  console.log('\n🔍 第二步：优先行规则 + 碰撞检测...')
  
  const branchOffsets = new Map() // branchName -> offset
  branchOffsets.set('M', 0) // M主线不移动

  // 顶层分支"最新优先行"规则：
  // 从 M 主线分出的顶层 L 分支，按出现顺序排列，越新的越靠上（row 1），
  // 之前的顶层分支依次整体下移一行。
  const topLevelBranches = (branchChildren.get('M') || []).filter(Boolean)
  if (topLevelBranches.length > 0) {
    topLevelBranches.forEach((branchName, index) => {
      const baseShift = topLevelBranches.length - 1 - index // 最新的index最大，shift=0
      branchOffsets.set(branchName, baseShift)
      if (baseShift > 0) {
        console.log(`  ⬇️ 顶层分支预偏移: ${branchName} 下移 ${baseShift} 行 (最新优先)`) 
      } else {
        console.log(`  ⬆️ 顶层分支保持row 1: ${branchName}`)
      }
    })
  }

  // 按分支出现顺序处理（从后往前）
  for (let i = branchAppearOrder.length - 1; i >= 0; i--) {
    const currentBranch = branchAppearOrder[i]
    if (currentBranch === 'M') continue // M主线不检测

    const currentNodes = branchNodes.get(currentBranch) || []
    const currentOffset = branchOffsets.get(currentBranch) || 0
    
    console.log(`\n  检查分支 ${currentBranch} (${currentNodes.length}个节点, 当前offset=${currentOffset})`)

    // 检查是否与之前的分支冲突
    for (let j = 0; j < i; j++) {
      const earlierBranch = branchAppearOrder[j]
      if (earlierBranch === 'M') continue

      const earlierNodes = branchNodes.get(earlierBranch) || []
      const earlierOffset = branchOffsets.get(earlierBranch) || 0

      // 检查是否有列重叠
      for (const currentNodeId of currentNodes) {
        const currentCol = nodeCols.get(currentNodeId)
        const currentBaseRow = nodeRows.get(currentNodeId)
        const currentFinalRow = currentBaseRow + currentOffset

        for (const earlierNodeId of earlierNodes) {
          const earlierCol = nodeCols.get(earlierNodeId)
          const earlierBaseRow = nodeRows.get(earlierNodeId)
          const earlierFinalRow = earlierBaseRow + earlierOffset

          // 同列且行号冲突
          if (currentCol === earlierCol && Math.abs(currentFinalRow - earlierFinalRow) < 1) {
            // 需要下移早期分支（及其所有子分支）
            const newOffset = currentFinalRow - earlierBaseRow + 1
            
            if (newOffset > earlierOffset) {
              branchOffsets.set(earlierBranch, newOffset)
              console.log(`    ⬇️ ${earlierBranch} 需要避让 ${currentBranch}`)
              console.log(`      冲突: ${currentNodeId}(row=${currentFinalRow}) vs ${earlierNodeId}(row=${earlierFinalRow})`)
              console.log(`      ${earlierBranch} offset: ${earlierOffset} → ${newOffset}`)

              // 递归下移该分支的所有子分支
              const shiftChildBranches = (parentBranch, additionalOffset) => {
                const children = branchChildren.get(parentBranch) || []
                for (const childBranch of children) {
                  const childCurrentOffset = branchOffsets.get(childBranch) || 0
                  const childNewOffset = childCurrentOffset + additionalOffset
                  branchOffsets.set(childBranch, childNewOffset)
                  console.log(`      ⬇️⬇️ 子分支 ${childBranch} 跟随下移: ${childCurrentOffset} → ${childNewOffset}`)
                  // 递归处理孙分支
                  shiftChildBranches(childBranch, additionalOffset)
                }
              }

              shiftChildBranches(earlierBranch, newOffset - earlierOffset)
            }
          }
        }
      }
    }
  }

  // ========== 第三步：应用偏移量 ==========
  console.log('\n📐 第三步：应用偏移量...')
  for (const [nodeId, baseRow] of nodeRows.entries()) {
    const branchName = nodeBranch.get(nodeId)
    const offset = branchOffsets.get(branchName) || 0
    const finalRow = baseRow + offset
    nodeRows.set(nodeId, finalRow)
    console.log(`  ${nodeId}: baseRow=${baseRow} + offset=${offset} = finalRow=${finalRow} (分支=${branchName})`)
  }

  // ========== 第四步：转换为实际坐标 ==========
  storyStructure.nodes.forEach(node => {
    const row = nodeRows.get(node.id) || 0
    const col = nodeCols.get(node.id) || 0
    
    const x = baseX + col * horizontalSpacing
    const y = baseY + row * verticalSpacing
    
    nodePositions.set(node.id, { x, y })
  })

  console.log('✅ 布局计算完成')
  return nodePositions
}

  // 清理聊天记录
  const clearChatHistory = useCallback(() => {
    try {
      // 只有在编辑模式下才清理聊天记录
      if (!editingStoryId) {
        return
      }
      
      const chatKey = `ai-guided-chat-${editingStoryId}`
      localStorage.removeItem(chatKey)
      if (process.env.NODE_ENV === 'development') {
        console.debug('✅ 聊天记录已清理:', chatKey)
      }
    } catch (error) {
      // 静默失败
      if (process.env.NODE_ENV === 'development') {
        console.debug('⚠️ 清理聊天记录失败（已忽略）:', error.message)
      }
    }
  }, [editingStoryId])

  // 跳转到编辑器
  const handleJumpToEditor = async () => {
    let loadingToast
    let targetStoryId
    try {
      console.log('🚀 开始跳转到编辑器流程...')
      
      // 显示loading提示，避免用户以为卡死
      loadingToast = toast.loading('正在准备编辑器数据...')
      
      // 转换数据为编辑器格式（同步操作，快速完成）
      const editorData = convertToEditorData()
      console.log('✅ 编辑器数据转换完成:', editorData.nodes.length, '个节点')
      
      // 准备完整的AI故事数据（用于编辑器初始化）
      const aiStoryData = {
        title: confirmedData.outline?.title || '未命名故事',
        summary: confirmedData.outline?.summary || '',
        outline: confirmedData.outline,
        characters: confirmedData.characters?.characters || [],
        // 保存完整的分镜数据
        storyboard: confirmedData.storyboard,
        // 使用专门的场景数据（如果存在），否则从分镜转换
        scenes: confirmedData.scenes?.scenes || (confirmedData.storyboard?.shots || []).map(shot => ({
          // 从分镜转换为场景格式
          id: `scene-${shot.shotNumber}`,
          shotNumber: shot.shotNumber,
          chapter: shot.chapter || 1,
          scene: shot.scene || shot.shotNumber,
          title: shot.title || shot.sceneName,
          sceneName: shot.sceneName,
          description: shot.description,
          content: shot.content || shot.description,
          type: shot.type || 'normal',
          
          // 视觉元素
          imagePrompt: shot.imagePrompt,
          sceneImage: shot.sceneImage || shot.generatedImage || '',
          backgroundImage: shot.backgroundImage || shot.generatedImage || '',
          generatedImage: shot.generatedImage || '',
          imageFixed: shot.imageFixed || false,
          imageSource: shot.imageSource || 'none',
          
          // 镜头参数
          cameraAngle: shot.cameraAngle,
          duration: shot.duration,
          
          // 环境
          location: shot.location || '',
          timeOfDay: shot.timeOfDay || '',
          mood: shot.mood || '',
          background: shot.background || '',
          musicStyle: shot.musicStyle || '',
          
          // 角色和对话
          characters: shot.characters || [],
          selectedCharacters: shot.selectedCharacters || [],
          dialogues: shot.dialogues || [],
          
          // 分支
          choices: (shot.branches || []).map(b => ({
            id: b.id,
            text: b.text,
            targetNodeId: b.targetNodeId || `scene-${b.targetShotNumber}`,
            targetSceneId: b.targetSceneId || `scene-${b.targetShotNumber}`,
            targetShotNumber: b.targetShotNumber,
            path: b.path,
            consequence: b.consequence
          })),
          
          // 结局
          isEnding: shot.isEnding || false,
          endingType: shot.endingType || '',
          endingDescription: shot.endingDescription || ''
        })),
        genre: confirmedData.genre?.title || '',
        theme: confirmedData.theme?.title || '',
        style: confirmedData.style?.title || ''
      }
      
      // 根据是否为编辑模式，决定创建或更新（同步操作，存储到Zustand）
      if (isEditMode && editingStoryId) {
        console.log('📝 编辑模式：更新现有故事...')
        // 编辑模式：更新现有故事（Zustand同步更新）
        updateStory(editingStoryId, {
          title: aiStoryData.title,
          description: aiStoryData.summary,
          genre: aiStoryData.genre,
          theme: aiStoryData.theme,
          style: aiStoryData.style,
          nodes: editorData.nodes,
          edges: editorData.edges,
          characters: aiStoryData.characters,
          scenes: aiStoryData.scenes,
          metadata: {
            worldview: confirmedData.worldview,
            lastEditedWith: 'AI Guided Creation',
            lastEditedAt: new Date().toISOString()
          }
        })
        targetStoryId = editingStoryId
        console.log('✅ 故事已更新到Zustand store:', targetStoryId)
      } else {
        console.log('🆕 创建模式：创建新故事...')
        
        // 生成标签
        let tags = []
        try {
          tags = await TagGeneratorService.generateStoryTags({
            title: aiStoryData.title,
            description: aiStoryData.summary,
            genre: aiStoryData.genre,
            theme: aiStoryData.theme,
            style: aiStoryData.style,
            metadata: { summary: aiStoryData.summary }
          })
          console.log('🏷️ 已生成标签:', tags)
        } catch (error) {
          console.warn('⚠️ 标签生成失败，使用默认标签:', error)
          tags = ['原创', '精选']
        }
        
        // 创建模式：创建新故事（Zustand同步创建）
        const newStory = createStory({
          title: aiStoryData.title,
          description: aiStoryData.summary,
          genre: aiStoryData.genre,
          theme: aiStoryData.theme,
          style: aiStoryData.style,
          tags,
          nodes: editorData.nodes,
          edges: editorData.edges,
          characters: aiStoryData.characters,
          scenes: aiStoryData.scenes,
          metadata: {
            worldview: confirmedData.worldview,
            createdWith: 'AI Guided Creation',
            createdAt: new Date().toISOString()
          }
        })
        targetStoryId = newStory.id
        console.log('✅ 新故事已创建到Zustand store:', targetStoryId, '节点数:', editorData.nodes.length)
      }
      
      // 关闭loading提示
      toast.dismiss(loadingToast)
      toast.success(isEditMode ? '策划案已更新！' : '策划案已创建！')
      
      // 后台异步保存完整数据到IndexedDB（完全不阻塞，静默失败）
      // 注意：保存所有数据以保证用户体验，但完全不等待完成
      const aiStoryKey = `ai-story-extra-${targetStoryId}`
      setTimeout(() => {
        // 延迟执行，确保不影响导航
        // 保存完整的对话历史和数据，不精简
        const fullData = {
          chatHistory: chatMessages,           // 保存完整聊天记录
          conversationHistory: conversationHistory, // 保存完整对话历史
          confirmedData: confirmedData,        // 保存所有确认数据
          timestamp: new Date().toISOString()
        }
        
        console.log('📦 准备后台保存完整数据，大小约:', JSON.stringify(fullData).length, '字节')
        
        localCloudService.saveToIndexedDB(aiStoryKey, fullData)
          .then(() => {
            console.log('✅ 完整数据已保存到IndexedDB（后台）:', aiStoryKey)
          })
          .catch(error => {
            console.debug('⚠️ IndexedDB保存失败（不影响使用）:', error.message)
            // 如果IndexedDB失败，可以考虑分块保存或其他策略
          })
      }, 100) // 100ms延迟，让导航先执行
      
      // 立即跳转到编辑器，不等待任何存储操作
      console.log('🚀 正在跳转到编辑器:', `/editor/${targetStoryId}`)
      navigate(`/editor/${targetStoryId}`)
      
    } catch (error) {
      console.error('❌ 跳转到编辑器失败:', error)
      if (loadingToast) {
        toast.dismiss(loadingToast)
      }
      toast.error('跳转失败: ' + error.message)
      
      // 即使出错，如果已经有storyId，也尝试跳转
      if (targetStoryId) {
        console.log('⚠️ 出现错误但仍尝试跳转...')
        setTimeout(() => {
          navigate(`/editor/${targetStoryId}`)
        }, 500)
      }
    }
  }

  // 转换为编辑器数据（使用智能布局算法）
  const convertToEditorData = () => {
    const nodes = []
    const edges = []
    const originalShots = confirmedData.storyboard?.shots || []
    const storyboardShots = applyBranchingPolicies(originalShots)
    const shotNumberToNodeId = new Map()

    if (storyboardShots.length === 0) {
      console.warn('⚠️ 没有分镜数据')
      return { nodes: [], edges: [] }
    }

    console.log('🎬 开始转换分镜为编辑器数据，共', storyboardShots.length, '个镜头')
    
    // 节点类型统计（仅在调试模式下显示）
    const DEBUG_MODE = true // 设为 true 可查看详细日志
    
    if (DEBUG_MODE) {
      console.log('📋 confirmedData.storyboard:', confirmedData.storyboard)
      console.log('📋 storyboardShots:', storyboardShots)
      
      const nodeStats = {
        main: storyboardShots.filter(s => /^M\d+$/.test(s.sceneId)).length,
        L1Branches: storyboardShots.filter(s => /^L1-\d+$/.test(s.sceneId)).length,
        L2Branches: storyboardShots.filter(s => /^L2-\d+$/.test(s.sceneId)).length,
        L3Branches: storyboardShots.filter(s => /^L3-\d+$/.test(s.sceneId)).length,
        endings: storyboardShots.filter(s => /^E\d+$/.test(s.sceneId) || s.isEnding).length
      }
      console.log('🔍 节点类型统计:', nodeStats)
      
      // 详细分析每个shot的结构
      console.log('\n📸 详细节点信息:')
      storyboardShots.forEach((shot, index) => {
        const hasChoices = Array.isArray(shot.choices) && shot.choices.length > 0
        console.log(`${index + 1}. ${shot.sceneId || `scene-${shot.shotNumber}`}:`, {
          shotNumber: shot.shotNumber,
          type: shot.type,
          isEnding: shot.isEnding,
          hasBranch: shot.hasBranch,
          choicesCount: shot.choices?.length || 0,
          title: shot.title
        })
        
        if (hasChoices) {
          console.log(`   ├─ 选项:`)
          shot.choices.forEach((choice, ci) => {
            console.log(`   │  ${ci + 1}. "${choice.text}" → ${choice.nextScene || choice.targetNodeId || choice.targetSceneId || '❌ 无目标'}`)
          })
        }
      })
      
      // 检查是否有决策节点
      const decisionNodes = storyboardShots.filter(s => 
        s.type === 'decision' || 
        s.hasBranch === true || 
        (Array.isArray(s.choices) && s.choices.length > 0)
      )
      console.log(`\n🔀 决策节点数量: ${decisionNodes.length}`)
      if (decisionNodes.length === 0) {
        console.error('❌ 致命问题：没有找到任何决策节点！所有节点会连成一条线！')
        console.error('   可能原因：')
        console.error('   1. AI没有生成 choices 数组')
        console.error('   2. AI没有设置 hasBranch: true')
        console.error('   3. AI没有设置 type: "decision"')
      } else {
        console.log('✅ 找到决策节点:', decisionNodes.map(n => n.sceneId))
      }
      
      // 检查结局节点
      const endingNodes = storyboardShots.filter(s => 
        s.isEnding === true || 
        s.type === 'ending' ||
        /^E\d+$/.test(s.sceneId)
      )
      console.log(`\n🎬 结局节点数量: ${endingNodes.length}`)
      if (endingNodes.length === 0) {
        console.error('❌ 致命问题：没有找到任何结局节点！')
      } else {
        console.log('✅ 找到结局节点:', endingNodes.map(n => n.sceneId))
      }
    }

    // 分析故事结构
    const storyStructure = {
      nodes: [],
      connections: []
    }

    // 为每个镜头创建节点信息（使用sceneId或shotNumber）
    storyboardShots.forEach((shot) => {
      const nodeId = shot.sceneId || `scene-${shot.shotNumber}`
      shotNumberToNodeId.set(shot.shotNumber, nodeId)

      storyStructure.nodes.push({
        id: nodeId,
        shot: shot,
        hasBranch: shot.hasBranch || (shot.choices && shot.choices.length > 0) || (shot.branches && shot.branches.length > 0)
      })
    })

    // 分析连接关系（支持choices和branches两种格式）
    storyboardShots.forEach((shot, index) => {
      const sourceId = shot.sceneId || `scene-${shot.shotNumber}`
      
      // 检查是否是结局节点（多重检查）
      const isEndingNode = shot.isEnding === true || 
                          shot.type === 'ending' || 
                          /^E\d+$/.test(shot.sceneId)
      
      if (isEndingNode) {
        // 结局节点不创建任何出边
        return
      }

      // 优先使用choices（新的多分支格式）
      if (shot.choices && shot.choices.length > 0) {
        shot.choices.forEach((choice, choiceIndex) => {
          // 获取目标ID（尝试多个字段）
          const targetSceneId = choice.nextScene || choice.targetNodeId || choice.targetSceneId
          
          if (!targetSceneId) {
            console.error(`❌ 致命错误: ${sourceId} 的选项 "${choice.text}" 没有设置目标场景ID！`)
            console.error(`   choice数据:`, choice)
            return
          }
          
          // 查找目标节点
          const targetShot = storyboardShots.find(s => 
            s.sceneId === targetSceneId || 
            `scene-${s.shotNumber}` === targetSceneId
          )
          if (targetShot) {
            const targetId = targetShot.sceneId || `scene-${targetShot.shotNumber}`
            storyStructure.connections.push({
              from: sourceId,
              to: targetId,
              label: choice.text,
              emotionalType: choice.emotionalType
            })
            if (DEBUG_MODE) console.log(`  ✅ 连接: ${sourceId} -> ${targetId} (${choice.text})`)
          } else {
            // 分支目标节点不存在
            console.error(`❌ 分支连接错误: ${sourceId} 的选项 "${choice.text}" 指向不存在的节点 ${targetSceneId}`)
            console.error(`   可用的场景ID:`, storyboardShots.map(s => s.sceneId || `scene-${s.shotNumber}`))
            console.error(`   该选项数据:`, choice)
          }
        })
      } 
      // 兼容旧的branches格式
      else if (shot.hasBranch && shot.branches && shot.branches.length > 0) {
        shot.branches.forEach(branch => {
          const targetId = shotNumberToNodeId.get(branch.targetShotNumber)
          if (targetId) {
            storyStructure.connections.push({
              from: sourceId,
              to: targetId,
              label: branch.text
            })
          } else {
            // 分支目标节点不存在，记录错误
            console.warn(`⚠️ 分支连接错误: ${sourceId} 的选项 "${branch.text}" 指向不存在的节点 ${branch.targetShotNumber}`)
          }
        })
      } 
      // 无分支且非结局，需要智能判断连接到哪里
      else if (!shot.isEnding && index < storyboardShots.length - 1) {
        // 检查当前节点类型（使用新的L系列命名）
        const isMainNode = /^M\d+$/.test(shot.sceneId) // M1, M2, M3...
        const isBranchNode = /^L\d+-\d+$/.test(shot.sceneId) // L1-1, L2-1, L3-1...
        
        if (isBranchNode) {
          // L系列支线节点：查找同一支线的下一个节点
          const branchMatch = shot.sceneId.match(/^(L\d+)-(\d+)$/)
          if (branchMatch) {
            const branchSeries = branchMatch[1] // L1, L2, L3...
            const branchIndex = parseInt(branchMatch[2]) // 1, 2, 3...
            
            // 查找同一支线的下一个节点（L1-2 -> L1-3）
            const nextInSameBranch = storyboardShots.slice(index + 1).find(s => {
              const match = s.sceneId?.match(/^(L\d+)-(\d+)$/)
              return match && match[1] === branchSeries && parseInt(match[2]) === branchIndex + 1
            })
            
            if (nextInSameBranch) {
              const targetId = nextInSameBranch.sceneId
              storyStructure.connections.push({
                from: sourceId,
                to: targetId,
                label: ''
              })
              if (DEBUG_MODE) console.log(`  ➡️ 支线内连接: ${sourceId} -> ${targetId}`)
            } else {
              // 支线末端，智能查找对应的结局节点
              // L1支线 -> E2, L2支线 -> E3, L3支线 -> E4, L4支线 -> E5
              const branchNumber = parseInt(branchSeries.replace('L', '')) // L1 -> 1, L2 -> 2...
              const expectedEndingId = `E${branchNumber + 1}` // L1 -> E2, L2 -> E3...
              
              // 先尝试找到对应编号的结局
              let targetEndingNode = storyboardShots.find(s => s.sceneId === expectedEndingId)
              
              // 如果找不到对应编号的结局，尝试找下一个未被使用的结局
              if (!targetEndingNode) {
                const usedEndings = new Set(
                  storyStructure.connections
                    .filter(c => /^E\d+$/.test(c.to))
                    .map(c => c.to)
                )
                targetEndingNode = storyboardShots.find(s => 
                  (s.isEnding || /^E\d+$/.test(s.sceneId)) && 
                  !usedEndings.has(s.sceneId)
                )
              }
              
              if (targetEndingNode) {
                const targetId = targetEndingNode.sceneId
                storyStructure.connections.push({
                  from: sourceId,
                  to: targetId,
                  label: '到达结局'
                })
                if (DEBUG_MODE) console.log(`  🏁 支线到结局: ${sourceId} -> ${targetId} (${branchSeries}支线 -> ${targetId})`)
              } else {
                console.warn(`  ⚠️ ${sourceId} (${branchSeries}支线末端) 无法找到对应的结局节点`)
              }
            }
          }
        } else if (isMainNode) {
          // M系列主线节点：连接到下一个主线节点
          const mainIndex = parseInt(shot.sceneId.match(/^M(\d+)$/)[1])
          const nextMainNode = storyboardShots.slice(index + 1).find(s => {
            const match = s.sceneId?.match(/^M(\d+)$/)
            return match && parseInt(match[1]) === mainIndex + 1
          })
          
          if (nextMainNode) {
            const targetId = nextMainNode.sceneId
            storyStructure.connections.push({
              from: sourceId,
              to: targetId,
              label: ''
            })
            if (DEBUG_MODE) console.log(`  ➡️ 主线连接: ${sourceId} -> ${targetId}`)
          } else {
            // 主线末端，智能查找 E1 结局（主线专属）
            let targetEndingNode = storyboardShots.find(s => s.sceneId === 'E1')
            
            // 如果找不到 E1，尝试找第一个未被使用的结局
            if (!targetEndingNode) {
              const usedEndings = new Set(
                storyStructure.connections
                  .filter(c => /^E\d+$/.test(c.to))
                  .map(c => c.to)
              )
              targetEndingNode = storyboardShots.find(s => 
                (s.isEnding || /^E\d+$/.test(s.sceneId)) && 
                !usedEndings.has(s.sceneId)
              )
            }
            
            if (targetEndingNode) {
              const targetId = targetEndingNode.sceneId
              storyStructure.connections.push({
                from: sourceId,
                to: targetId,
                label: '到达结局'
              })
              if (DEBUG_MODE) console.log(`  🏁 主线到结局: ${sourceId} -> ${targetId} (主线专属结局)`)
            } else {
              console.warn(`  ⚠️ ${sourceId} (主线末端) 无法找到结局节点`)
            }
          }
        } else {
          // 其他节点：连接到下一个节点
          const nextShot = storyboardShots[index + 1]
          if (nextShot && !nextShot.isEnding) {
            const targetId = nextShot.sceneId || `scene-${nextShot.shotNumber}`
            storyStructure.connections.push({
              from: sourceId,
              to: targetId,
              label: ''
            })
            if (DEBUG_MODE) console.log(`  ➡️ 默认连接: ${sourceId} -> ${targetId}`)
          }
        }
      }
    })

    // 修复孤立的决策点（包括 M4, M5 等）
    const allDecisionNodes = storyboardShots.filter(s => 
      s.type === 'decision' && 
      s.choices && s.choices.length > 0
    )
    
    let fixedDecisionCount = 0
    allDecisionNodes.forEach((decisionNode, decisionIndex) => {
      const decisionId = decisionNode.sceneId
      const hasIncomingConnection = storyStructure.connections.some(conn => conn.to === decisionId)
      
      if (!hasIncomingConnection) {
        // 找到这个决策点在分镜中的位置
        const decisionShotIndex = storyboardShots.findIndex(s => s.sceneId === decisionId)
        
        // 向前查找最近的、没有主路径出边的非结局节点
        for (let i = decisionShotIndex - 1; i >= 0; i--) {
          const candidateShot = storyboardShots[i]
          const candidateId = candidateShot.sceneId
          
          // 跳过结局节点
          if (candidateShot.isEnding || /^E\d+$/.test(candidateId)) {
            continue
          }
          
          // 检查该节点是否已经有主路径出边（不包括分支选择）
          const hasMainOutgoing = storyStructure.connections.some(conn => 
            conn.from === candidateId && 
            (!conn.label || conn.label === '' || conn.label === '继续')
          )
          
          if (!hasMainOutgoing) {
            // 找到了合适的连接源
            storyStructure.connections.push({
              from: candidateId,
              to: decisionId,
              label: ''
            })
            fixedDecisionCount++
            if (DEBUG_MODE) console.log(`  🔗 修复孤立决策点: ${candidateId} -> ${decisionId}`)
            break // 找到一个就停止
          }
        }
      }
    })
    if (fixedDecisionCount > 0) {
      console.log(`✅ 已修复 ${fixedDecisionCount} 个孤立决策点`)
    }
    
    // 修复孤立的结局节点 - 改进版（防止结局之间相互连接）
    const endingNodes = storyboardShots.filter(s => s.isEnding || /^E\d+$/.test(s.sceneId))
    const orphanedEndings = []
    
    endingNodes.forEach(ending => {
      const endingId = ending.sceneId || `scene-${ending.shotNumber}`
      const hasIncomingConnection = storyStructure.connections.some(conn => conn.to === endingId)
      
      if (!hasIncomingConnection) {
        orphanedEndings.push(ending)
      }
    })
    
    let fixedEndingCount = 0
    if (orphanedEndings.length > 0) {
      if (DEBUG_MODE) console.warn(`⚠️ 发现 ${orphanedEndings.length} 个孤立结局:`, orphanedEndings.map(e => e.sceneId))
      
      // 查找所有无出边的非结局节点（潜在的连接源）
      const potentialSources = storyboardShots.filter(shot => {
        // 必须排除所有结局节点
        if (shot.isEnding || /^E\d+$/.test(shot.sceneId)) {
          return false
        }
        // 排除已有分支选择的节点
        if (shot.choices && shot.choices.length > 0) {
          return false
        }
        
        const shotId = shot.sceneId || `scene-${shot.shotNumber}`
        const hasOutgoingConnection = storyStructure.connections.some(conn => conn.from === shotId)
        return !hasOutgoingConnection // 只要无出边的节点
      })
      
      if (DEBUG_MODE) console.log(`  📍 可用的连接源节点:`, potentialSources.map(s => s.sceneId))
      
      // 将孤立结局分配给可用的源节点
      orphanedEndings.forEach((ending, index) => {
        const endingId = ending.sceneId || `scene-${ending.shotNumber}`
        
        if (index < potentialSources.length) {
          const sourceShot = potentialSources[index]
          const sourceId = sourceShot.sceneId || `scene-${sourceShot.shotNumber}`
          
          // 再次确认源节点不是结局节点
          if (!sourceShot.isEnding && !/^E\d+$/.test(sourceShot.sceneId)) {
            storyStructure.connections.push({
              from: sourceId,
              to: endingId,
              label: '到达结局'
            })
            fixedEndingCount++
            if (DEBUG_MODE) console.log(`  🔗 自动连接: ${sourceId} -> ${endingId}`)
          } else {
            console.warn(`  ⚠️ 跳过：${sourceId} 是结局节点，不能作为连接源`)
          }
        } else {
          console.warn(`  ❌ 无法为结局 ${endingId} 找到合适的非结局连接源`)
        }
      })
      
      if (fixedEndingCount > 0) {
        console.log(`✅ 已修复 ${fixedEndingCount} 个孤立结局`)
      }
    }
    
    // 分支完整性验证
    const branchValidation = validateBranchIntegrity(storyboardShots, storyStructure.connections)
    if (!branchValidation.isValid) {
      console.warn('⚠️ 分支完整性验证失败:', branchValidation.errors)
    }
    
    // 检测支线合并逻辑
    const mergeAnalysis = detectBranchMerge(storyboardShots, storyStructure.connections)
    if (DEBUG_MODE) {
      console.log('📊 支线合并分析:', {
        合并点数量: mergeAnalysis.mergePoints.length,
        分支路径数量: mergeAnalysis.branchPaths.size,
        合并点详情: mergeAnalysis.mergePoints.map(mp => `${mp.branchSource} → ${mp.nodeId}`)
      })
    }
    
    // 验证和修复连接逻辑
    const connectionValidation = validateConnections(storyboardShots, storyStructure.connections)
    if (!connectionValidation.isValid) {
      console.warn('⚠️ 连接验证失败:', connectionValidation.errors)
    }

    if (DEBUG_MODE) {
      console.log('📊 故事结构分析:', {
        节点数: storyStructure.nodes.length,
        连接数: storyStructure.connections.length
      })
    }

    // 使用智能布局算法计算节点位置
    // 🔧 规范化数据格式,确保与布局算法兼容
    const normalizedStructure = {
      nodes: storyStructure.nodes.map(n => ({
        id: n.id,
        type: n.shot?.type || 'scene',
        data: {
          sceneId: n.shot?.sceneId || n.id,
          label: n.shot?.title || n.id,
          isEnding: n.shot?.isEnding || n.shot?.type === 'ending' || /^E\d+$/.test(n.id)
        }
      })),
      connections: storyStructure.connections.map(c => ({
        from: c.from,
        to: c.to,
        label: c.label
      }))
    }
    
    if (DEBUG_MODE) {
      console.log('📊 规范化后的数据结构:', {
        节点数: normalizedStructure.nodes.length,
        连接数: normalizedStructure.connections.length,
        节点ID列表: normalizedStructure.nodes.map(n => n.id),
        连接列表: normalizedStructure.connections.map(c => `${c.from} -> ${c.to}`)
      })
    }
    
    const nodePositions = calculateNodePositions(normalizedStructure)
    console.log(`✅ 节点布局计算完成 (${storyboardShots.length}个节点)`)

    // 创建节点（使用计算出的位置）
    storyboardShots.forEach((shot, index) => {
      const nodeId = shot.sceneId || `scene-${shot.shotNumber}`
      shotNumberToNodeId.set(shot.shotNumber, nodeId)

      const position = nodePositions.get(nodeId) || { x: 250 + (index % 3) * 300, y: 100 + Math.floor(index / 3) * 200 }
      
      if (DEBUG_MODE) {
        console.log(`🎯 创建节点 ${index + 1}/${storyboardShots.length}: ${nodeId}`, {
          position: position,
          fromCalculation: nodePositions.has(nodeId),
          fallback: !nodePositions.has(nodeId),
          sceneId: shot.sceneId,
          shotNumber: shot.shotNumber,
          isEnding: shot.isEnding,
          hasChoices: shot.choices?.length > 0
        })
      }

      nodes.push({
        id: nodeId,
        type: 'storyNode',
        position: position,
        data: {
          // 基础信息（对应分镜）
          title: shot.title || shot.sceneName || `镜头${shot.shotNumber}`,
          description: shot.description || '',
          content: shot.content || shot.description || '',
          type: shot.type || (shot.isEnding ? 'ending' : (shot.hasBranch ? 'branch' : 'normal')),
          
          // 分镜标识
          shotNumber: shot.shotNumber,
          chapter: shot.chapter || 1,
          scene: shot.scene || shot.shotNumber,
          sceneId: shot.sceneId || nodeId,
          sceneName: shot.sceneName || shot.title || `场景${shot.shotNumber}`,
          
          // 视觉元素（对应分镜图片）
          imagePrompt: shot.imagePrompt || '',
          sceneImage: shot.sceneImage || shot.generatedImage || shot.backgroundImage || '',
          backgroundImage: shot.backgroundImage || shot.generatedImage || shot.sceneImage || '',
          generatedImage: shot.generatedImage || shot.sceneImage || '',
          imageFixed: shot.imageFixed || false,
          imageSource: shot.imageSource || 'none',
          
          // 镜头技术参数
          cameraAngle: shot.cameraAngle || '中景',
          duration: shot.duration || '5秒',
          
          // 环境设定（对应场景）
          location: shot.location || '',
          timeOfDay: shot.timeOfDay || '',
          mood: shot.mood || '',
          atmosphere: shot.atmosphere || shot.mood || '',
          background: shot.background || '',
          musicStyle: shot.musicStyle || '',
          
          // 角色和对话（完全对应）
          characters: shot.characters || [],
          selectedCharacters: shot.selectedCharacters || [],
          dialogues: (shot.dialogues || []).map(d => ({
            id: d.id || `dialogue-${shot.shotNumber}-${Math.random()}`,
            characterId: d.characterId,
            characterName: d.characterName,
            content: d.content,
            emotion: d.emotion || '平静',
            order: d.order || 0
          })),
          
          // 分支逻辑（关键：用于节点连接）
          hasBranches: shot.hasBranch || (shot.branches && shot.branches.length > 0) || (shot.choices && shot.choices.length > 0) || false,
          isBranch: shot.isBranch || shot.hasBranch || (shot.choices && shot.choices.length > 0) || false,
          branches: shot.choices && shot.choices.length > 0 
            ? shot.choices.map((c, idx) => ({
                id: c.id || `choice-${shot.sceneId || shot.shotNumber}-${idx}`,
                text: c.text || '',
                targetNodeId: c.nextScene || c.targetNodeId || c.targetSceneId,
                targetSceneId: c.nextScene || c.targetSceneId || c.targetNodeId,
                emotionalType: c.emotionalType,
                consequence: c.consequence || `选择了"${c.text}"`
              }))
            : (shot.branches || []).map(b => ({
                id: b.id || `branch-${shot.shotNumber}-${b.targetShotNumber}`,
                text: b.text || '',
                targetNodeId: b.targetNodeId || `scene-${b.targetShotNumber}`,
                targetSceneId: b.targetSceneId || `scene-${b.targetShotNumber}`,
                targetShotNumber: b.targetShotNumber,
                path: b.path || b.text?.replace(/\s/g, '-').toLowerCase() || `path-${b.targetShotNumber}`,
                consequence: b.consequence || `选择了"${b.text}"`
              })),
          // 规范化choices数组，确保包含targetNodeId
          choices: (shot.choices || []).map((c, idx) => ({
            ...c,
            id: c.id || `choice-${shot.sceneId || shot.shotNumber}-${idx}`,
            targetNodeId: c.nextScene || c.targetNodeId || c.targetSceneId,
            targetSceneId: c.nextScene || c.targetSceneId || c.targetNodeId
          })),
          
          // 节点句柄配置
          handles: generateNodeHandles(shot),
          
          // 结局信息
          isEnding: shot.isEnding || false,
          endingType: shot.endingType || '',
          endingDescription: shot.endingDescription || '',
          
          // 保留原始分镜数据
          originalShot: shot
        }
      })
    })

    // 创建边（基于分支和镜头号）
    let edgeCount = 0
    storyboardShots.forEach((shot, index) => {
      const sourceNodeId = shot.sceneId || `scene-${shot.shotNumber}`
      
      if (DEBUG_MODE) {
        console.log(`\n🔍 处理Shot ${index + 1}/${storyboardShots.length} (${sourceNodeId}):`)
        console.log('  - sceneId:', shot.sceneId)
        console.log('  - shotNumber:', shot.shotNumber)
        console.log('  - hasChoices:', shot.choices?.length > 0)
        console.log('  - choices:', shot.choices)
        console.log('  - hasBranches:', shot.branches?.length > 0)
        console.log('  - isEnding:', shot.isEnding)
        console.log('  - type:', shot.type)
      }
      
      // 优先使用choices（新的多分支格式）
      if (shot.choices && shot.choices.length > 0) {
        if (DEBUG_MODE) console.log(`  ✅ 使用choices格式，共${shot.choices.length}个选择`)
        shot.choices.forEach((choice, choiceIndex) => {
          if (DEBUG_MODE) {
            console.log(`    🎯 处理选择 ${choiceIndex + 1}:`, {
              text: choice.text,
              nextScene: choice.nextScene,
              emotionalType: choice.emotionalType
            })
          }
          
          // 查找目标节点 - 改进查找逻辑
          const targetShot = storyboardShots.find(s => {
            // 优先匹配sceneId
            if (s.sceneId && choice.nextScene) {
              return s.sceneId === choice.nextScene
            }
            // 其次匹配scene-{shotNumber}格式
            if (choice.nextScene && choice.nextScene.startsWith('scene-')) {
              const targetShotNumber = parseInt(choice.nextScene.replace('scene-', ''))
              return s.shotNumber === targetShotNumber
            }
            // 最后尝试直接匹配shotNumber
            if (choice.nextScene && !isNaN(parseInt(choice.nextScene))) {
              return s.shotNumber === parseInt(choice.nextScene)
            }
            return false
          })
          
          if (targetShot) {
            const targetNodeId = targetShot.sceneId || `scene-${targetShot.shotNumber}`
            if (DEBUG_MODE) {
              console.log(`    ✅ 找到目标节点: ${targetNodeId}`)
              console.log(`    🔗 创建边: ${sourceNodeId} -> ${targetNodeId}`)
              console.log(`       handle: branch-right-${choice.id || `choice-${sourceNodeId}-${choiceIndex}`}`)
            }
            edges.push({
              id: `e-${sourceNodeId}-${targetNodeId}-${choiceIndex}`,
              source: sourceNodeId,
              sourceHandle: `branch-right-${choice.id || `choice-${sourceNodeId}-${choiceIndex}`}`, // 使用分支选项的绿色输出端
              target: targetNodeId,
              targetHandle: null, // 目标节点的输入端
              label: choice.text || `选项${choiceIndex + 1}`,
              type: 'branch', // 使用分支边类型
              animated: true,
              markerEnd: { type: 'arrowclosed', color: '#10B981' }, // 绿色箭头
              style: { stroke: '#10B981', strokeWidth: 2 }, // 绿色连线
              data: {
                choiceId: choice.id,
                emotionalType: choice.emotionalType,
                consequence: choice.consequence,
                isBranchConnection: true
              }
            })
            edgeCount++
          } else {
            console.warn(`❌ 未找到目标节点: ${sourceNodeId} -> ${choice.nextScene}`)
            if (DEBUG_MODE) {
              console.log(`    🔍 可用的sceneId:`, storyboardShots.map(s => s.sceneId))
              console.log(`    🔍 可用的shotNumber:`, storyboardShots.map(s => s.shotNumber))
            }
          }
        })
      }
      // 兼容旧的branches格式
      else if (shot.hasBranch && shot.branches && shot.branches.length > 0) {
        shot.branches.forEach((branch, branchIndex) => {
          const targetNodeId = shotNumberToNodeId.get(branch.targetShotNumber)
          if (sourceNodeId && targetNodeId) {
            edges.push({
              id: `e-${sourceNodeId}-${targetNodeId}-${branchIndex}`,
              source: sourceNodeId,
              sourceHandle: `branch-${branchIndex}`, // 使用分支选项的句柄
              target: targetNodeId,
              targetHandle: 'input', // 目标节点的输入端
              label: branch.text || `选项${branchIndex + 1}`,
              type: 'branch', // 使用分支边类型
              animated: true,
              markerEnd: { type: 'arrowclosed', color: '#8B5CF6' },
              style: { stroke: '#8B5CF6', strokeWidth: 2 },
              data: {
                branchId: branch.id,
                path: branch.path,
                consequence: branch.consequence,
                isBranchConnection: true
              }
            })
            edgeCount++
          }
        })
      }
      
      // 处理线性连接：非结局节点且没有choices的节点
      const hasActualChoices = shot.choices && shot.choices.length > 0
      if (!shot.isEnding && !hasActualChoices && index < storyboardShots.length - 1) {
        // 连接到下一个镜头
        const nextShot = storyboardShots[index + 1]
        const targetNodeId = nextShot.sceneId || `scene-${nextShot.shotNumber}`
        
        if (DEBUG_MODE) console.log(`    ➡️ 检查线性连接: ${sourceNodeId} -> ${targetNodeId}`)
        
        // 检查目标节点是否已经被其他分支指向
        const isTargetReachedByBranch = storyboardShots.some(s => {
          if (s.choices && s.choices.length > 0) {
            return s.choices.some(choice => 
              choice.nextScene === nextShot.sceneId || 
              choice.nextScene === `scene-${nextShot.shotNumber}`
            )
          }
          if (s.branches && s.branches.length > 0) {
            return s.branches.some(branch => 
              branch.targetShotNumber === nextShot.shotNumber
            )
          }
          return false
        })
        
        if (isTargetReachedByBranch) {
          if (DEBUG_MODE) console.log(`      ⏭️ 跳过：目标节点 ${targetNodeId} 已被分支指向`)
        } else {
          // 创建线性连接
          edges.push({
            id: `e-${sourceNodeId}-${targetNodeId}`,
            source: sourceNodeId,
            sourceHandle: null, // 使用默认输出端
            target: targetNodeId,
            targetHandle: null, // 使用默认输入端
            type: 'default',
            markerEnd: { type: 'arrowclosed', color: '#64748B' },
            style: { stroke: '#64748B', strokeWidth: 2 },
            data: {
              isMainConnection: true
            }
          })
          edgeCount++
          if (DEBUG_MODE) console.log(`      ✅ 创建线性连接: ${sourceNodeId} -> ${targetNodeId}`)
        }
      }
    })

    console.log(`✅ 转换完成: ${nodes.length}个节点, ${edges.length}条连接`)
    
    if (DEBUG_MODE) {
      console.log('📊 详细统计:', {
        节点数: nodes.length,
        边数: edges.length,
        分支节点数: nodes.filter(n => n.data.hasBranches).length,
        结局节点数: nodes.filter(n => n.data.isEnding).length,
        节点预览: nodes.slice(0, 2).map(n => ({ id: n.id, title: n.data.title })),
        边预览: edges.slice(0, 2).map(e => ({ source: e.source, target: e.target }))
      })
    }
    if (DEBUG_MODE) {
      console.log('📊 最终统计:')
      console.log('  - 节点数:', nodes.length)
      console.log('  - 边数:', edges.length)
      console.log('  - 节点位置:', nodes.map(n => ({ id: n.id, x: n.position.x, y: n.position.y, branches: n.data.branches?.length })))
      console.log('  - 边列表:', edges.map(e => ({ from: e.source, to: e.target, handle: e.sourceHandle })))
    }
    
    return { nodes, edges }
  }

    // 分镜决策规范化：每个决策点最多2个选项，并根据三种分支模式自动补全
    function applyBranchingPolicies(shots) {
      try {
        const cloned = JSON.parse(JSON.stringify(shots || []))
        const idToShot = new Map()
        cloned.forEach(s => { idToShot.set(s.sceneId || `scene-${s.shotNumber}`, s) })

        console.log('\n🔧 开始应用分支策略规范化...')
        
        // ========== 第一步：清理错误的结局标记和错误命名的节点 ==========
        console.log('\n🧹 清理错误的结局标记和错误节点...')
        const nodesToDelete = []
        const wrongEndingNodes = [] // 记录被错误标记为结局的节点
        
        cloned.forEach((shot, index) => {
          const sceneId = shot.sceneId
          
          // 只有E开头且数字的节点才能是结局
          if (/^E\d+$/.test(sceneId)) {
            shot.isEnding = true
            shot.type = 'ending'
            console.log(`  ✅ 确认结局节点: ${sceneId}`)
          } else {
            // 清除错误的结局标记
            if (shot.isEnding || shot.type === 'ending') {
              console.error(`  ❌ 错误: ${sceneId} 被标记为结局，但不符合 E 系列命名规范！`)
              console.error(`     AI应该使用 E1, E2, E3... 作为结局节点，而不是 ${sceneId}`)
              
              wrongEndingNodes.push({
                sceneId: sceneId,
                title: shot.title,
                endingType: shot.endingType,
                endingDescription: shot.endingDescription
              })
              
              shot.isEnding = false
              shot.type = shot.choices && shot.choices.length > 0 ? 'decision' : 'normal'
              
              // 清理结局相关字段
              delete shot.endingType
              delete shot.endingDescription
              
              console.warn(`  🔧 已修正: ${sceneId} → type="${shot.type}", isEnding=false`)
            }
          }
          
          // 删除错误命名的结局节点（EL12, EL22等）
          if (/^EL\d+$/.test(sceneId)) {
            console.error(`  ❌ 标记删除错误命名的结局节点: ${sceneId}`)
            nodesToDelete.push(index)
          }
          
          // 删除A开头的节点（AI错误生成的"后续节点"）
          if (/^A\d+$/.test(sceneId)) {
            console.error(`  ❌ 标记删除错误的A系列节点: ${sceneId}`)
            nodesToDelete.push(index)
          }
          
          // 🆕 检测但不删除不规范命名的节点，只报警告
          if (/^[LM]\d+(?:-\d+)*_\d+$/.test(sceneId)) {
            console.error(`  ❌ 错误: 节点 ${sceneId} 使用了不规范的命名格式！`)
            console.error(`     应该使用 L3-1, L4-1 等格式，而不是 ${sceneId}`)
            console.error(`     这会导致流程图无法正确显示，请检查AI提示词`)
            // 不删除，保留AI的剧情内容
          }
        })
        
        // 从后往前删除（避免索引变化）
        for (let i = nodesToDelete.length - 1; i >= 0; i--) {
          const index = nodesToDelete[i]
          const deletedNode = cloned.splice(index, 1)[0]
          idToShot.delete(deletedNode.sceneId)
          console.log(`  🗑️ 已删除: ${deletedNode.sceneId}`)
        }
        
        // 重建映射
        idToShot.clear()
        cloned.forEach(s => idToShot.set(s.sceneId, s))

        // 工具：创建节点
        const ensureNode = (id, config = {}) => {
          if (!idToShot.has(id)) {
            const shot = {
              sceneId: id,
              id,
              shotNumber: cloned.length + 1,
              title: config.title || `节点${id}`,
              description: config.description || '',
              content: config.content || '',
              type: config.type || 'normal',
              isEnding: config.isEnding || false,
              hasBranch: config.hasBranch || false,
              isBranch: config.isBranch || false,
              choices: config.choices || [],
              branches: config.branches || [],
              ...config
            }
            cloned.push(shot)
            idToShot.set(id, shot)
            console.log(`  ✅ 自动创建节点: ${id} (${shot.type})`)
          }
          return idToShot.get(id)
        }

        // 检查是否有决策节点
        const decisionNodes = cloned.filter(s => 
          s.type === 'decision' || 
          s.hasBranch === true || 
          (Array.isArray(s.choices) && s.choices.length > 0)
        )
        
        if (decisionNodes.length === 0) {
          console.error('❌ 致命问题：AI没有生成任何决策节点！')
          console.log('  🔧 尝试自动修复：将M3节点设为决策节点')
          
          // 找到M3节点，强制设为决策节点
          const m3 = cloned.find(s => s.sceneId === 'M3')
          if (m3) {
            m3.type = 'decision'
            m3.hasBranch = true
            m3.isBranch = true
            m3.choices = [
              {
                id: 'choice-M3-1',
                text: '继续主线剧情',
                nextScene: 'M4',
                targetNodeId: 'M4',
                targetSceneId: 'M4',
                emotionalType: 'determined'
              },
              {
                id: 'choice-M3-2',
                text: '探索支线剧情',
                nextScene: 'L1-1',
                targetNodeId: 'L1-1',
                targetSceneId: 'L1-1',
                emotionalType: 'curious'
              }
            ]
            console.log('  ✅ 已将M3设为决策节点')
          }
        }

        // 处理每个决策节点
        cloned.forEach((shot) => {
          const sceneId = shot.sceneId || `scene-${shot.shotNumber}`
          const isDecision = Array.isArray(shot.choices) && shot.choices.length > 0
          if (!isDecision) return

          console.log(`\n  🔀 处理决策节点: ${sceneId}`)

          // 确保是决策类型
          shot.type = 'decision'
          shot.hasBranch = true
          shot.isBranch = true

          // 检查选项数量，如果超过2个说明AI没有遵守约束
          if (shot.choices.length > 2) {
            console.error(`❌ 决策节点 ${sceneId} 有${shot.choices.length}个选项，违反了"每个决策点只有2个选项"的约束！`)
            console.error(`   这会导致部分分支内容无法展示，破坏故事体验。`)
            console.error(`   所有选项:`, shot.choices.map(c => `"${c.text}" → ${c.nextScene || c.targetNodeId || '?'}`))
            console.error(`   建议：优化AI提示词，确保生成时就只有2个选项。`)
            // 不裁剪，保留所有选项，让编辑器显示警告
          }

          // 若不足2个，尝试自动补一个选项
          if (shot.choices.length === 1) {
            console.log(`  ⚠️ 只有1个选项，自动补充第2个`)
            shot.choices.push({ 
              text: '继续剧情', 
              id: `${sceneId}-auto-2`,
              nextScene: '',
              targetNodeId: '',
              targetSceneId: ''
            })
          }

          // 决定分支模式
          const mode = shot.branchingMode || inferBranchingMode(sceneId)
          const branchA = shot.choices[0]
          const branchB = shot.choices[1]

          if (/^M\d+$/.test(sceneId)) {
            // 主线决策：A继续主线，B开新L分支
            const currentIndex = parseInt(sceneId.replace('M',''))
            const nextMainId = `M${currentIndex + 1}`
            
            // A: 继续主线
            let targetA = branchA.targetNodeId || branchA.targetSceneId || branchA.nextScene
            
            // 如果没有目标，或者目标不是M系列，自动设置为下一个M节点
            if (!targetA || !/^M\d+$/.test(targetA)) {
              targetA = nextMainId
              console.log(`  🔧 选项A 修正目标 → ${targetA}`)
            } else {
              console.log(`  ✅ 选项A 已有正确目标 → ${targetA}`)
            }
            
            branchA.nextScene = targetA
            branchA.targetNodeId = targetA
            branchA.targetSceneId = targetA
            
            // B: 新起L分支
            let targetB = branchB.targetNodeId || branchB.targetSceneId || branchB.nextScene
            
            // 如果没有目标，或者目标不是L系列，自动设置为新L支线
            if (!targetB || !/^L\d+(?:-\d+)*$/.test(targetB)) {
            const newBranchNum = computeNextTopLevelBranchNumber(cloned)
              targetB = `L${newBranchNum}-1`
              console.log(`  🔧 选项B 修正目标 → ${targetB}`)
            } else {
              console.log(`  ✅ 选项B 已有正确目标 → ${targetB}`)
            }
            
            branchB.nextScene = targetB
            branchB.targetNodeId = targetB
            branchB.targetSceneId = targetB

            // 确保目标节点存在
            if (targetA && !idToShot.has(targetA)) {
              ensureNode(targetA, { type: 'normal', title: `主线节点${targetA}` })
            }
            if (targetB && !idToShot.has(targetB)) {
              ensureNode(targetB, { type: 'normal', title: `支线节点${targetB}` })
            }

          } else if (/^L\d+(?:-\d+)*$/.test(sceneId)) {
            // L分支决策：A继续当前分支，B开新的支线
            const parts = sceneId.split('-')
            const lastNum = parseInt(parts[parts.length - 1])
            const siblingNext = `${parts.slice(0, -1).join('-')}-${lastNum + 1}`
            
            // A: 继续当前支线
            let targetA = branchA.targetNodeId || branchA.targetSceneId || branchA.nextScene
            if (!targetA) {
              targetA = siblingNext
              console.log(`  🔧 选项A 自动设置 → ${targetA}`)
            } else {
              console.log(`  ✅ 选项A 已有目标 → ${targetA}`)
            }
            
            branchA.nextScene = targetA
            branchA.targetNodeId = targetA
            branchA.targetSceneId = targetA
            
            // B: 新起支线
            let targetB = branchB.targetNodeId || branchB.targetSceneId || branchB.nextScene
            
            if (!targetB) {
              // 只有在没有目标时，才自动设置
            const newBranchNum = computeNextTopLevelBranchNumber(cloned)
              targetB = `L${newBranchNum}-1`
              console.log(`  🔧 选项B 无目标，自动设置 → ${targetB}`)
            } else {
              // 检查目标是否存在和格式是否规范
              const targetExists = idToShot.has(targetB)
              const targetIsValidFormat = /^L\d+(?:-\d+)*$/.test(targetB)
              
              if (!targetIsValidFormat) {
                console.error(`  ❌ 选项B 目标格式不规范: ${targetB}`)
                console.error(`     应该使用 L3-1, L4-1 等格式`)
                console.error(`     请修正AI提示词，让AI生成正确的命名！`)
                // 不修改，保留AI的原始目标
              } else if (!targetExists) {
                console.warn(`  ⚠️ 选项B 目标节点不存在: ${targetB}`)
                console.warn(`     AI设置了目标但没有创建对应的节点`)
              } else {
                console.log(`  ✅ 选项B 已有正确目标 → ${targetB}`)
              }
            }
            
            branchB.nextScene = targetB
            branchB.targetNodeId = targetB
            branchB.targetSceneId = targetB
            
            // 只为不存在的节点创建空节点（用于避免连接错误）
            if (targetA && !idToShot.has(targetA)) {
              ensureNode(targetA, { type: 'normal', title: `支线节点${targetA}` })
              console.warn(`  ⚠️ 自动创建空节点: ${targetA}，但没有剧情内容！`)
            }
            if (targetB && !idToShot.has(targetB)) {
              ensureNode(targetB, { type: 'normal', title: `支线节点${targetB}` })
              console.warn(`  ⚠️ 自动创建空节点: ${targetB}，但没有剧情内容！`)
            }
          }

          // 统一所有choice的目标字段
          shot.choices.forEach(choice => {
            const target = choice.nextScene || choice.targetNodeId || choice.targetSceneId
            if (target) {
              choice.nextScene = target
              choice.targetNodeId = target
              choice.targetSceneId = target
            }
          })
        })

        // ========== 关键修复：检测并修复"决策点直接连接结局"的问题 ==========
        console.log('\n🔍 检测决策点是否直接连接结局...')
        
        cloned.forEach(shot => {
          const sceneId = shot.sceneId
          if (!shot.choices || shot.choices.length === 0) return
          
          shot.choices.forEach((choice, choiceIndex) => {
            const target = choice.nextScene || choice.targetNodeId || choice.targetSceneId
            if (!target) return
            
            // 检查目标是否是结局节点
            if (/^E\d+$/.test(target)) {
              console.error(`❌ 决策节点 ${sceneId} 的选项${choiceIndex + 1} "${choice.text}" 直接连接到结局 ${target}`)
              console.error(`   这违反了"决策点不能直接连接结局"的原则！`)
              
              // 自动修复：插入中间节点
              const intermediateNodeId = `${sceneId}-to-${target}`
              console.log(`   🔧 自动修复：插入中间节点 ${intermediateNodeId}`)
              
              // 创建中间节点
              ensureNode(intermediateNodeId, {
                type: 'normal',
                title: `通往${target}的过渡节点`,
                content: `剧情发展中...`,
                imagePrompt: shot.imagePrompt || ''
              })
              
              // 修改choice指向中间节点
              choice.nextScene = intermediateNodeId
              choice.targetNodeId = intermediateNodeId
              choice.targetSceneId = intermediateNodeId
              
              console.log(`   ✅ 已修改：${sceneId} 选项${choiceIndex + 1} → ${intermediateNodeId} → ${target}`)
            }
          })
        })
        
        // ========== 检测并删除孤立的L支线 ==========
        console.log('\n🔍 检测孤立的L支线（没有入口的支线）...')
        
        // 1. 收集所有L支线
        const lBranches = new Set()
        cloned.forEach(s => {
          const match = (s.sceneId || '').match(/^(L\d+)/)
          if (match) lBranches.add(match[1])
        })
        console.log(`  📊 发现 ${lBranches.size} 个L支线: ${Array.from(lBranches).join(', ')}`)
        
        // 2. 收集所有有入边的节点（被choices指向的节点）
        const nodesWithIncomingEdges = new Set()
        cloned.forEach(shot => {
          if (shot.choices && shot.choices.length > 0) {
            shot.choices.forEach(choice => {
              const target = choice.nextScene || choice.targetNodeId || choice.targetSceneId
              if (target) {
                nodesWithIncomingEdges.add(target)
              }
            })
          }
        })
        
        // 3. 检查每个L支线的起点是否有入边
        const orphanedBranches = []
        lBranches.forEach(branch => {
          const firstNode = `${branch}-1`
          if (!nodesWithIncomingEdges.has(firstNode)) {
            console.error(`  ❌ 孤立支线: ${branch} (${firstNode} 没有任何决策点指向它)`)
            orphanedBranches.push(branch)
          } else {
            console.log(`  ✅ 有效支线: ${branch} (${firstNode} 有入边)`)
          }
        })
        
        // 4. 删除孤立支线的所有节点
        if (orphanedBranches.length > 0) {
          console.warn(`\n⚠️ 发现 ${orphanedBranches.length} 个孤立支线，将自动删除以保证故事完整性`)
          console.warn(`   孤立支线列表: ${orphanedBranches.join(', ')}`)
          console.warn(`   原因: AI生成了这些支线节点，但没有创建指向它们的决策点`)
          console.warn(`   解决: 已自动删除这些孤立节点，避免在编辑器中出现无法到达的场景`)
          
          const nodesToRemove = []
          cloned.forEach((shot, index) => {
            const sceneId = shot.sceneId || shot.id
            orphanedBranches.forEach(branch => {
              if (sceneId.startsWith(branch + '-')) {
                console.log(`    🗑️ 删除孤立支线节点: ${sceneId}`)
                nodesToRemove.push(index)
              }
            })
          })
          
          // 从后往前删除，避免索引错乱
          nodesToRemove.sort((a, b) => b - a).forEach(index => {
            const deleted = cloned.splice(index, 1)[0]
            idToShot.delete(deleted.sceneId)
          })
          
          // 从lBranches中移除孤立支线
          orphanedBranches.forEach(branch => {
            lBranches.delete(branch)
            console.log(`    ✅ 已从支线列表中移除: ${branch}`)
          })
          
          console.log(`  ✅ 清理完成，剩余有效支线: ${Array.from(lBranches).join(', ') || '无'}`)
        } else {
          console.log(`  ✅ 所有L支线都有入口，无需清理`)
        }
        
        // ========== 确保每条有效L支线都有结局，并连接末尾节点 ==========
        console.log('\n🔗 为有效支线创建结局节点...')
        
        // 为每个有效L支线创建结局节点
        const branchEndingMap = new Map() // 记录每个支线对应的结局ID
        Array.from(lBranches).sort().forEach((branch, index) => {
          const endingId = `E${index + 2}` // E1是主线，E2开始是L支线
          branchEndingMap.set(branch, endingId)
          
          ensureNode(endingId, {
            type: 'ending',
            isEnding: true,
            title: `${branch}支线结局`,
            endingType: 'Normal'
          })
          console.log(`  ✅ 确保结局节点: ${branch} → ${endingId}`)
        })

        // 确保主线有E1结局
        ensureNode('E1', {
          type: 'ending',
          isEnding: true,
          title: '主线结局',
          endingType: 'Happy'
        })
        console.log(`  ✅ 确保结局节点: M主线 → E1`)
        
        // 🆕 报告AI生成的错误结局节点
        if (wrongEndingNodes.length > 0) {
          console.error(`\n❌ AI生成了 ${wrongEndingNodes.length} 个错误的结局节点！`)
          console.error(`   AI应该使用 E1, E2, E3... 作为结局节点，而不是剧情节点！`)
          console.error(`   错误的结局节点列表:`)
          wrongEndingNodes.forEach(node => {
            console.error(`     - ${node.sceneId} (${node.title})`)
          })
          console.warn(`   ✅ 已自动修正: 这些节点已被改为普通节点，并创建了正确的 E 系列结局节点`)
        }
        
        // ========== 自动连接支线末尾节点到对应结局 ==========
        console.log('\n🔗 自动连接支线末尾节点到结局...')
        
        Array.from(lBranches).forEach((branch) => {
          const endingId = branchEndingMap.get(branch)
          const branchNodes = cloned.filter(s => 
            (s.sceneId || '').startsWith(branch) && 
            !s.isEnding &&
            s.sceneId !== endingId
          )
          
          if (branchNodes.length === 0) {
            console.warn(`  ⚠️ ${branch}支线没有任何非结局节点`)
            return
          }
          
          // 找到该支线的末尾节点（没有被支线内其他节点引用的节点）
          const branchNodeIds = branchNodes.map(n => n.sceneId)
          const referencedIds = new Set()
          
          // 收集所有被引用的节点
          branchNodes.forEach(node => {
            if (node.choices) {
              node.choices.forEach(c => {
                const target = c.nextScene || c.targetNodeId || c.targetSceneId
                if (target && branchNodeIds.includes(target)) {
                  referencedIds.add(target)
                }
              })
            }
          })
          
          // 末尾节点 = 在支线内但不被支线内其他节点引用的节点
          const lastNodes = branchNodes.filter(n => !referencedIds.has(n.sceneId))
          
          if (lastNodes.length === 0) {
            console.warn(`  ⚠️ ${branch}支线找不到末尾节点`)
            return
          }
          
          console.log(`  📍 ${branch}支线的末尾节点: ${lastNodes.map(n => n.sceneId).join(', ')}`)
          
          // 为每个末尾节点添加到结局的连接
          lastNodes.forEach(lastNode => {
            // 检查是否已经有choices
            if (!lastNode.choices) {
              lastNode.choices = []
            }
            
            // 检查是否已经有连接到结局的choice
            const hasEndingChoice = lastNode.choices.some(c => {
              const target = c.nextScene || c.targetNodeId || c.targetSceneId
              return target === endingId
            })
            
            // 检查是否有连接到其他支线（跨支线连接）
            const hasCrossBranchChoice = lastNode.choices.some(c => {
              const target = c.nextScene || c.targetNodeId || c.targetSceneId
              return target && !target.startsWith(branch) && !target.startsWith('E')
            })
            
            if (!hasEndingChoice && !hasCrossBranchChoice) {
              // 如果没有choices，说明是线性节点，在convertToEditorData中会自动连接
              if (lastNode.choices.length === 0) {
                console.log(`  ✅ ${lastNode.sceneId} 是线性节点，将自动连接到 ${endingId}`)
              } else {
                // 如果有choices但没有连接到结局，添加一个
                console.log(`  🔧 ${lastNode.sceneId} 有choices但没有连到结局，添加连接到 ${endingId}`)
                lastNode.choices.push({
                  id: `auto-ending-${lastNode.sceneId}`,
                  text: '继续',
                  nextScene: endingId,
                  targetNodeId: endingId,
                  targetSceneId: endingId
                })
              }
            } else if (hasEndingChoice) {
              console.log(`  ✅ ${lastNode.sceneId} 已经连接到结局`)
            } else if (hasCrossBranchChoice) {
              console.log(`  ↗️ ${lastNode.sceneId} 连接到其他支线`)
            }
          })
        })
        

        console.log(`\n✅ 分支策略规范化完成`)
        console.log(`   原始节点数: ${shots.length}`)
        console.log(`   处理后节点数: ${cloned.length}`)
        console.log(`   节点ID列表: ${cloned.map(s => s.sceneId).join(', ')}`)
        
        // 统计结局节点
        const endingCount = cloned.filter(s => s.isEnding || /^E\d+$/.test(s.sceneId)).length
        console.log(`   结局节点数: ${endingCount}`)

        return cloned
      } catch (e) {
        console.warn('⚠️ applyBranchingPolicies 失败，返回原始shots', e)
        return shots || []
      }
    }

    function computeNextTopLevelBranchNumber(shots) {
      const nums = shots
        .map(s => (s.sceneId || '').match(/^L(\d+)/))
        .filter(Boolean)
        .map(m => parseInt(m[1]))
      const max = nums.length ? Math.max(...nums) : 0
      return max + 1
    }

    function inferBranchingMode(sceneId) {
      // 三种模式关键字，可后续接设置面板
      // 1) main_and_branch_to_ending
      // 2) branch_and_newbranch_to_ending
      // 3) branch_merge_main_shared_ending
      if (/^M\d+$/.test(sceneId)) return 'main_and_branch_to_ending'
      return 'branch_and_newbranch_to_ending'
    }

  // 计算完成进度（排除第一步"创意想法"）
  const completedCount = Object.keys(confirmedData).filter(key => key !== 'idea').length
  const totalSteps = steps.length - 1 // 排除第一步
  const showContinueButton = (selectedOption && steps[currentStep]?.needsChoice) || 
                            (currentGeneratedContent && !steps[currentStep]?.needsChoice)

  return (
    <div className="fixed inset-0 bg-dark-900 flex flex-col pt-16">
      {/* 左右分栏容器 - 全屏 */}
      <div className="flex-1 flex overflow-hidden">
        {/* 左侧：AI对话区 */}
        <div className="flex-1 flex flex-col border-r border-dark-600">
          {/* 对话历史 - 可独立滚动 */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {chatMessages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`${
                  msg.isAdvice ? 'max-w-[95%]' : 'max-w-[80%]'
                } rounded-lg px-4 py-3 ${
                  msg.role === 'user'
                    ? 'bg-primary text-white'
                    : msg.isAdvice
                    ? 'bg-gradient-to-r from-purple-900/40 to-pink-900/40 border border-purple-500/30 text-dark-100'
                    : 'bg-dark-700 text-dark-200'
                }`}
              >
                <div className={`text-sm whitespace-pre-wrap ${msg.isAdvice ? 'leading-relaxed' : ''}`}>
                  {msg.content}
                </div>
                <div className="text-xs opacity-50 mt-1">
                  {msg.timestamp.toLocaleTimeString()}
                </div>
              </div>
            </div>
          ))}

          {/* 当前选项卡片 */}
          {currentOptions.length > 0 && (
            <div className="relative mb-4">
              {showContinueButton && selectedOption && (
                <div className="absolute -top-10 right-0 z-10">
                  <button
                    onClick={handleContinue}
                    className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold shadow-lg"
                  >
                    继续 →
                  </button>
                </div>
              )}

              <div className="grid gap-3 mt-8">
                {currentOptions.map(option => (
                  <div
                    key={option.id}
                    onClick={() => handleSelectOption(option)}
                    className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      selectedOption?.id === option.id
                        ? 'border-primary bg-primary/10'
                        : 'border-dark-600 bg-dark-700 hover:border-primary/50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-white font-semibold">{option.title}</h4>
                      <span className="text-yellow-400 text-sm">⭐ {option.score}/10</span>
                    </div>
                    <p className="text-dark-300 text-sm">{option.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 当前生成内容 */}
          {currentGeneratedContent && (
            <div className="bg-dark-700 rounded-lg p-6 relative">
              {showContinueButton && (
                <div className="absolute top-4 right-4 z-10">
                  <button
                    onClick={handleContinue}
                    className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-semibold shadow-lg"
                  >
                    继续 →
                  </button>
                </div>
              )}

              <h3 className="text-white font-bold text-lg mb-4 flex items-center">
                <span className="mr-2">{steps[currentStep].icon}</span>
                {currentGeneratedContent?.title || '生成内容'}
              </h3>

              {/* 角色设计 */}
              {currentGeneratedContent?.type === 'characters' && currentGeneratedContent?.characters && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {currentGeneratedContent.characters.map((char, idx) => (
                    <div key={idx} className="bg-dark-600 rounded-lg p-4 space-y-3 flex flex-col">
                      {/* 图片预览区域 */}
                      {char.generatedImage ? (
                        <div className="mb-3">
                          <img
                            key={`${char.id}-${char.generatedImage}-${forceUpdate}`}
                            src={ImageProxyService.shouldUseProxy(char.generatedImage) 
                              ? ImageProxyService.getProxiedImageUrl(char.generatedImage)
                              : char.generatedImage}
                            alt={char.name}
                            className="w-full h-48 object-cover rounded-lg"
                            onLoad={() => console.log('✅ 角色图片加载成功:', char.name, char.generatedImage)}
                            onError={async (e) => {
                              console.error('❌ 角色图片加载失败:', char.name, char.generatedImage, e)
                              
                              // 如果当前已经是代理URL，尝试直接URL
                              if (e.target.src.includes('/proxy/image')) {
                                console.log('🔄 代理URL失败，尝试直接URL:', char.generatedImage)
                                e.target.src = char.generatedImage
                                return
                              }
                              
                              // 如果当前是直接URL，尝试代理URL
                              if (e.target.src === char.generatedImage) {
                                try {
                                  const proxiedUrl = ImageProxyService.getProxiedImageUrl(char.generatedImage)
                                  console.log('🔄 直接URL失败，尝试代理URL:', proxiedUrl)
                                  
                                  // 检查代理服务器是否可用
                                  const proxyAvailable = await ImageProxyService.checkProxyServer()
                                  if (proxyAvailable) {
                                    console.log('✅ 代理服务器可用，使用代理URL')
                                    e.target.src = proxiedUrl
                                  } else {
                                    console.warn('⚠️ 代理服务器不可用，显示错误提示')
                                    e.target.style.display = 'none'
                                    e.target.nextSibling.style.display = 'block'
                                  }
                                } catch (error) {
                                  console.error('❌ 代理重试失败:', error)
                                  e.target.style.display = 'none'
                                  e.target.nextSibling.style.display = 'block'
                                }
                              } else {
                                // 其他情况，直接显示错误
                                console.warn('⚠️ 图片加载完全失败，显示错误提示')
                                e.target.style.display = 'none'
                                e.target.nextSibling.style.display = 'block'
                              }
                            }}
                          />
                          <div className="text-xs text-red-400 mt-1" style={{ display: 'none' }}>
                            ❌ 图片加载失败，请检查网络连接或代理服务器
                          </div>
                          <div className="text-xs text-green-400 mt-1">
                            ✅ 图像已生成 | URL: {char.generatedImage.substring(0, 50)}...
                          </div>
                          <div className="text-xs text-blue-400 mt-1">
                            🔄 强制更新计数: {forceUpdate} | 角色ID: {char.id}
                          </div>
                        </div>
                      ) : (
                        <div className="mb-3 bg-dark-800 rounded-lg flex items-center justify-center border-2 border-dashed border-dark-500" style={{ aspectRatio: '1/1' }}>
                          <div className="text-center text-dark-400">
                            <div className="text-3xl mb-2">🖼️</div>
                            <div className="text-sm">1:1 角色设计</div>
                            <div className="text-xs mt-1">点击下方按钮生成</div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-semibold text-lg">{char.name}</h4>
                          <span className="text-primary text-sm">{char.type}</span>
                        </div>
                        <button
                          onClick={async () => {
                            if (!char.generatedImage) {
                              console.log('🎨 开始生成角色图像:', char.name, char.imagePrompt)
                              try {
                                const generatedImage = await generateImage(char.imagePrompt, '1:1') // 角色使用1:1比例
                                console.log('✅ 图像生成成功，URL:', generatedImage)
                                
                                // 使用辅助函数更新状态
                                updateCharacterImage(char.id, generatedImage)
                                
                              } catch (error) {
                                console.error('❌ 角色图像生成失败:', error)
                                toast.error('图像生成失败: ' + error.message)
                              }
                            }
                          }}
                          className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          disabled={!!char.generatedImage}
                        >
                          {char.generatedImage ? '✅ 已生成' : '🎨 生成立绘'}
                        </button>
                      </div>
                      <p className="text-dark-300 text-sm">{char.description}</p>
                      <div className="text-xs text-dark-400">
                        <div className="mb-2">
                          <span className="text-primary">性格：</span>
                          {char.personality}
                        </div>
                        <div className="p-2 bg-dark-800 rounded">
                          <span className="text-primary">📝 文生图提示词：</span>
                          <div className="mt-1 text-dark-300">{char.imagePrompt}</div>
                        </div>
                      </div>
                      <div className="flex items-center justify-between pt-2 border-t border-dark-600">
                        <span className="text-xs text-dark-400">
                          🎤 {char.voiceType}
                          {char.selectedVoice && (
                            <span className="text-green-400 ml-2">✓ 已选择</span>
                          )}
                        </span>
                        <button
                          onClick={() => mockSelectVoice(char)}
                          className="px-3 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
                        >
                          {char.selectedVoice ? '更换配音' : '选择配音'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 场景设计 */}
              {currentGeneratedContent?.type === 'scenes' && currentGeneratedContent?.scenes && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                  {currentGeneratedContent.scenes.map((scene, idx) => (
                    <div key={idx} className="bg-dark-600 rounded-lg p-4 space-y-3 flex flex-col">
                      {/* 图片预览区域 */}
                      {scene.generatedImage ? (
                        <div className="mb-3">
                          <img
                            key={`${scene.id}-${scene.generatedImage}-${forceUpdate}`}
                            src={scene.generatedImage}
                            alt={scene.name}
                            className="w-full h-40 object-cover rounded-lg"
                            onLoad={() => console.log('✅ 场景图片加载成功:', scene.name, scene.generatedImage)}
                            onError={(e) => console.error('❌ 场景图片加载失败:', scene.name, scene.generatedImage, e)}
                          />
                        </div>
                      ) : (
                        <div className="mb-3 bg-dark-800 rounded-lg flex items-center justify-center border-2 border-dashed border-dark-500" style={{ aspectRatio: '16/9' }}>
                          <div className="text-center text-dark-400">
                            <div className="text-3xl mb-2">🏞️</div>
                            <div className="text-sm">16:9 场景设计</div>
                            <div className="text-xs mt-1">点击下方按钮生成</div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <h4 className="text-white font-semibold text-lg">{scene.name}</h4>
                        <button
                          onClick={async () => {
                            if (!scene.generatedImage) {
                              console.log('🎨 开始生成场景图像:', scene.name, scene.imagePrompt)
                              try {
                                const generatedImage = await generateImage(scene.imagePrompt, '16:9') // 场景使用16:9比例
                                console.log('✅ 场景图像生成成功，URL:', generatedImage)
                                
                                // 使用辅助函数更新状态
                                updateSceneImage(scene.id, generatedImage)
                                
                              } catch (error) {
                                console.error('❌ 场景图像生成失败:', error)
                                toast.error('图像生成失败: ' + error.message)
                              }
                            }
                          }}
                          className="px-3 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          disabled={!!scene.generatedImage}
                        >
                          {scene.generatedImage ? '✅ 已生成' : '🎨 生成概念图'}
                        </button>
                      </div>
                      <p className="text-dark-300 text-sm">{scene.description}</p>
                      <div className="text-xs text-dark-400">
                        <div className="mb-2">
                          <span className="text-primary">氛围：</span>
                          {scene.atmosphere}
                        </div>
                        <div className="p-2 bg-dark-800 rounded mb-2">
                          <span className="text-primary">📝 文生图提示词：</span>
                          <div className="mt-1 text-dark-300">{scene.imagePrompt}</div>
                        </div>
                      </div>
                      <div className="space-y-2 pt-2 border-t border-dark-600">
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-dark-400">
                            🎵 {scene.musicStyle}
                            {scene.generatedMusic && (
                              <span className="text-green-400 ml-2">✓ 已生成</span>
                            )}
                          </span>
                          <button
                            onClick={() => {
                              mockGenerateMusic(scene)
                              setCurrentGeneratedContent(prev => {
                                if (!prev || !prev.scenes) {
                                  console.warn('⚠️ currentGeneratedContent或scenes为空，无法更新')
                                  return prev
                                }
                                return {
                                  ...prev,
                                  scenes: prev.scenes.map(s =>
                                    s.id === scene.id
                                      ? { ...s, generatedMusic: `mock-music-${scene.id}.mp3` }
                                      : s
                                  )
                                }
                              })
                            }}
                            className="px-3 py-1 bg-purple-500 text-white rounded text-xs hover:bg-purple-600"
                          >
                            {scene.generatedMusic ? '更换音乐' : '生成音乐'}
                          </button>
                        </div>
                        <div className="text-xs text-dark-400">
                          <span className="text-primary">🔊 音效：</span>
                          {scene.soundEffects}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* 分镜设计 */}
              {currentGeneratedContent?.type === 'storyboard' && currentGeneratedContent?.shots && (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {currentGeneratedContent.shots.map((shot, idx) => (
                    <div key={idx} className="bg-dark-600 rounded-lg p-4 space-y-2">
                      {/* 图片预览区域 */}
                      {shot.generatedImage ? (
                        <div className="mb-2">
                          <img
                            src={shot.generatedImage}
                            alt={`镜头${shot.shotNumber}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                        </div>
                      ) : (
                        <div className="mb-2 bg-dark-800 h-32 rounded-lg flex items-center justify-center border-2 border-dashed border-dark-500">
                          <div className="text-center text-dark-400">
                            <div className="text-2xl mb-1">🎬</div>
                            <div className="text-xs">分镜画面预览</div>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="text-white font-semibold">镜头 {shot.shotNumber}</h4>
                          <span className="text-primary text-xs">{shot.sceneName}</span>
                        </div>
                        <button
                          onClick={async () => {
                            if (!shot.generatedImage) {
                              const generatedImage = await generateImage(shot.imagePrompt, '16:9') // 分镜使用16:9比例
                              setCurrentGeneratedContent(prev => {
                                if (!prev || !prev.shots) {
                                  console.warn('⚠️ currentGeneratedContent或shots为空，无法更新')
                                  return prev
                                }
                                return {
                                  ...prev,
                                  shots: prev.shots.map(s =>
                                    s.shotNumber === shot.shotNumber
                                      ? { ...s, generatedImage: generatedImage }
                                      : s
                                  )
                                }
                              })
                            }
                          }}
                          className="px-2 py-1 bg-blue-500 text-white rounded text-xs hover:bg-blue-600"
                          disabled={!!shot.generatedImage}
                        >
                          {shot.generatedImage ? '✅ 已生成' : '🎨 生成'}
                        </button>
                      </div>
                      <p className="text-dark-300 text-sm">{shot.description}</p>
                      <div className="text-xs text-dark-400 space-y-2">
                        <div>
                          <span className="text-primary">📷</span> {shot.cameraAngle} |{' '}
                          <span className="text-primary">⏱️</span> {shot.duration}
                        </div>
                        {shot.dialogues && shot.dialogues.length > 0 && (
                          <div className="text-xs text-dark-500">
                            💬 {shot.dialogues.length}段对话
                          </div>
                        )}
                        {shot.hasBranch && shot.branches && (
                          <div className="text-xs text-blue-400">
                            🌿 {shot.branches.length}个分支选项
                          </div>
                        )}
                        {shot.choices && shot.choices.length > 0 && (
                          <div className="text-xs text-blue-400">
                            🌿 {shot.choices.length}个分支选择
                          </div>
                        )}
                        {shot.isEnding && (
                          <div className="text-xs text-green-400">
                            🏁 {shot.endingType || '结局'}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* 输入区域 - 固定在底部 */}
        <div className="flex-shrink-0 p-6 border-t border-dark-600 bg-dark-800">
          <div className="flex gap-3 mb-3">
            <input
              type="text"
              value={currentMessage}
              onChange={e => setCurrentMessage(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && sendMessage()}
              placeholder="输入消息或提出修改建议..."
              className="flex-1 bg-dark-600 border border-dark-500 rounded-lg px-4 py-2 text-white text-sm placeholder-dark-400 focus:border-primary focus:outline-none"
              disabled={isChatting}
            />
            <button
              onClick={sendMessage}
              disabled={!currentMessage.trim() || isChatting}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              发送
            </button>
          </div>

          <div className="text-dark-400 text-xs text-center">
            当前环节：{steps[currentStep].name} {steps[currentStep].icon}
            {showContinueButton && ' | 已准备好，点击右上方「继续」按钮'}
          </div>
        </div>
      </div>

      {/* 右侧：内容策划案 */}
      <div className="flex-1 flex flex-col bg-dark-800">
        {/* 固定顶部标题和进度 */}
        <div className="flex-shrink-0 p-6 border-b border-dark-600">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-white flex items-center">
                <span className="mr-2">📝</span>
                内容策划案
                {isEditMode && (
                  <span className="ml-3 px-3 py-1 bg-purple-500/20 text-purple-400 text-sm rounded-full border border-purple-500/30">
                    编辑模式
                  </span>
                )}
              </h2>
              <p className="text-dark-400 text-sm mt-1">
                已完成 {completedCount}/{totalSteps} 项
                {isEditMode && <span className="ml-2 text-purple-400">· 从编辑器返回</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* 手动刷新按钮 */}
              <button
                onClick={() => {
                  const newValue = forceUpdate + 1
                  setForceUpdate(newValue)
                  toast.success('已刷新策划案视图', { duration: 1500, icon: '🔄' })
                  console.log('🔄 手动刷新策划案，forceUpdate:', newValue)
                }}
                className="px-3 py-2 bg-white/5 hover:bg-white/10 text-white rounded-lg text-sm flex items-center gap-2 transition-all"
                title="手动刷新策划案视图"
              >
                <span className="text-base">🔄</span>
                刷新
              </button>
              {onClose && (
                <button
                  onClick={onClose}
                  className="text-dark-400 hover:text-white text-xl"
                >
                  ✕
                </button>
              )}
            </div>
          </div>

          {/* 环节进度条 - 横向排列 */}
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            {steps.slice(1).map((step, idx) => {
              const stepIndex = idx + 1
              const isCompleted = confirmedData[step.key]
              const isCurrent = stepIndex === currentStep
              
              return (
                <React.Fragment key={step.id}>
                  <div className="flex-shrink-0">
                    <div className={`flex flex-col items-center min-w-[80px] transition-all ${
                      isCompleted ? 'opacity-100' :
                      isCurrent ? 'opacity-100' :
                      'opacity-50'
                    }`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg mb-1 transition-all ${
                        isCompleted ? 'bg-green-500 text-white shadow-lg shadow-green-500/30' :
                        isCurrent ? 'bg-primary text-white shadow-lg shadow-primary/30 animate-pulse' :
                        'bg-dark-700 text-dark-400'
                      }`}>
                        {isCompleted ? '✓' : step.icon}
                      </div>
                      <span className={`text-xs text-center whitespace-nowrap ${
                        isCompleted ? 'text-green-400 font-semibold' :
                        isCurrent ? 'text-primary font-semibold' :
                        'text-dark-400'
                      }`}>
                        {step.name}
                      </span>
                    </div>
                  </div>
                  {/* 连接线 - 在环节之间 */}
                  {idx < steps.slice(1).length - 1 && (
                    <div className={`h-0.5 w-6 flex-shrink-0 ${
                      isCompleted ? 'bg-green-500' : 
                      isCurrent ? 'bg-primary' : 
                      'bg-dark-600'
                    }`} />
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>

        {/* 策划内容 - 可独立滚动 */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-4" key={`plan-${forceUpdate}`}>

          {steps.map(step => {
            const value = confirmedData[step.key]
            if (!value) return null

            // 使用时间戳作为key的一部分，确保数据更新时重新渲染
            const valueKey = value.__updatedAt || step.id

            return (
              <div
                key={`${step.id}-${valueKey}`}
                className="bg-dark-700 rounded-lg p-4 border border-primary/30"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold flex items-center text-lg">
                    <span className="text-green-500 mr-2">✓</span>
                    <span className="mr-2">{step.icon}</span>
                    {step.name}
                  </h3>
                </div>

                <div className="text-dark-300 text-sm">
                  {/* 需要选择的步骤 */}
                  {step.needsChoice && typeof value === 'object' && value.title ? (
                    <div className="space-y-2">
                      <div className="font-semibold text-white">{value.title}</div>
                      <div className="text-yellow-400 text-xs">⭐ {value.score}/10</div>
                      <div>{value.description}</div>
                    </div>
                  ) : typeof value === 'string' ? (
                    <div>{value}</div>
                  ) : value.type === 'characters' ? (
                    /* 角色设计详细显示 */
                    <div className="space-y-3">
                      <div className="text-primary text-xs mb-2">
                        共 {value.characters.length} 个角色
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {value.characters.map((char, idx) => (
                          <div key={idx} className="bg-dark-600 rounded p-3 space-y-2 flex flex-col">
                          {/* 角色立绘图片 - 9:16构图 */}
                          {char.generatedImage && (
                            <div className="mb-2">
                              <img
                                src={char.generatedImage}
                                alt={char.name}
                                className="w-full object-cover rounded-lg cursor-pointer hover:opacity-90"
                                style={{ aspectRatio: '1/1' }}
                                onClick={() => window.open(char.generatedImage, '_blank')}
                                title="点击查看大图"
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-semibold">{char.name}</div>
                              <div className="text-primary text-xs">{char.type}</div>
                            </div>
                            {char.generatedImage && (
                              <span className="text-green-400 text-xs">✓ 已生成立绘</span>
                            )}
                          </div>
                          <div className="text-xs text-dark-400">{char.description}</div>
                          <div className="text-xs text-dark-500">
                            性格：{char.personality}
                          </div>

                          {/* 配音播放 */}
                          {char.selectedVoice && (
                            <div className="pt-2 border-t border-dark-500">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-dark-400">
                                  🎤 {char.voiceType}
                                </span>
                                <button
                                  onClick={() =>
                                    toast('播放配音：' + char.selectedVoice.name, {
                                      icon: '▶️'
                                    })
                                  }
                                  className="px-2 py-1 bg-purple-500/50 text-white rounded text-xs hover:bg-purple-500"
                                >
                                  ▶️ 试听
                                </button>
                              </div>
                            </div>
                          )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : value.type === 'scenes' ? (
                    /* 场景设计详细显示 */
                    <div className="space-y-3">
                      <div className="text-primary text-xs mb-2">
                        共 {value.scenes.length} 个场景
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {value.scenes.map((scene, idx) => (
                          <div key={idx} className="bg-dark-600 rounded p-3 space-y-2 flex flex-col">
                          {/* 场景概念图 */}
                          {scene.generatedImage && (
                            <div className="mb-2">
                              <img
                                src={scene.generatedImage}
                                alt={scene.name}
                                className="w-full h-32 object-cover rounded-lg cursor-pointer hover:opacity-90"
                                onClick={() => window.open(scene.generatedImage, '_blank')}
                                title="点击查看大图"
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div className="text-white font-semibold">{scene.name}</div>
                            {scene.generatedImage && (
                              <span className="text-green-400 text-xs">✓ 已生成概念图</span>
                            )}
                          </div>
                          <div className="text-xs text-dark-400">{scene.description}</div>
                          <div className="text-xs text-dark-500">
                            氛围：{scene.atmosphere}
                          </div>

                          {/* 音乐播放 */}
                          {scene.generatedMusic && (
                            <div className="pt-2 border-t border-dark-500">
                              <div className="flex items-center justify-between">
                                <span className="text-xs text-dark-400">
                                  🎵 {scene.musicStyle}
                                </span>
                                <button
                                  onClick={() =>
                                    toast('播放音乐：' + scene.musicStyle, { icon: '▶️' })
                                  }
                                  className="px-2 py-1 bg-purple-500/50 text-white rounded text-xs hover:bg-purple-500"
                                >
                                  ▶️ 播放
                                </button>
                              </div>
                            </div>
                          )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : value.type === 'storyboard' ? (
                    /* 分镜设计详细显示 */
                    <div className="space-y-3">
                      <div className="text-primary text-xs mb-2">
                        共 {value.shots.length} 个镜头
                      </div>
                      {value.shots.map((shot, idx) => (
                        <div key={idx} className="bg-dark-600 rounded p-3 space-y-2">
                          {/* 分镜画面图片 */}
                          {shot.generatedImage && (
                            <div className="mb-2">
                              <img
                                src={shot.generatedImage}
                                alt={`镜头${shot.shotNumber}`}
                                className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-90"
                                onClick={() => window.open(shot.generatedImage, '_blank')}
                                title="点击查看大图"
                              />
                            </div>
                          )}

                          <div className="flex items-center justify-between">
                            <div>
                              <div className="text-white font-semibold">
                                镜头 {shot.shotNumber}
                              </div>
                              <div className="text-primary text-xs">{shot.sceneName}</div>
                            </div>
                            <div className="flex items-center gap-2">
                              {shot.generatedImage && (
                                <span className="text-green-400 text-xs">✓ 已生成</span>
                              )}
                              {shot.isEnding && (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded">
                                  结局
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-dark-400">{shot.description}</div>
                          {shot.dialogues && shot.dialogues.length > 0 && (
                            <div className="text-xs text-dark-500">
                              💬 {shot.dialogues.length}段对话
                            </div>
                          )}
                          {shot.hasBranch && shot.branches && (
                            <div className="text-xs text-blue-400">
                              🌿 {shot.branches.length}个分支选项
                            </div>
                          )}
                          {shot.choices && shot.choices.length > 0 && (
                            <div className="text-xs text-blue-400">
                              🌿 {shot.choices.length}个分支选择
                            </div>
                          )}
                          {shot.isEnding && (
                            <div className="text-xs text-green-400">
                              🏁 {shot.endingType || '结局'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-primary text-xs">详细内容已生成</div>
                  )}
                </div>
              </div>
            )
          })}
          
          {/* 跳转到编辑器按钮 - 在滚动区域内 */}
          {completedCount === totalSteps && (
            <div className="mt-6 space-y-3 pb-6">
              <button
                onClick={handleJumpToEditor}
                className="w-full px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-semibold shadow-lg hover:shadow-purple-500/50 transition-all transform hover:scale-[1.02]"
              >
                {isEditMode ? '💾 保存并返回编辑器 →' : '🚀 跳转到编辑器 →'}
              </button>
              
              {isEditMode && (
                <button
                  onClick={() => navigate(`/editor/${editingStoryId}`)}
                  className="w-full px-6 py-3 bg-dark-600 text-white rounded-lg font-semibold hover:bg-dark-500 transition-all"
                >
                  ← 放弃修改，返回编辑器
                </button>
              )}
            </div>
          )}
          </div>
        </div>
      </div>
      </div>
    </div>
  )
}

export default AIGuidedCreation

